/**
 * RodeoDrawManagement — non-destructive ferris-wheel draw screen for a
 * single rodeo event.
 *
 * This is the rodeo draw flow (separate from the legacy jackpot team-
 * pairing screen, which is left untouched and will converge onto this
 * model later). It drives the placement API:
 *
 *   POST /draw-ops/event/:id/execute-placement  → generate / re-run draft
 *   GET  /draw-ops/event/:id/draft              → persisted draft
 *   GET  /draw-ops/event/:id/capacity           → performances + slot limits
 *   GET  /draw-ops/event/:id/conflicts          → conflict dashboard
 *   PATCH/draw-ops/event/:id/draft/entry/:eid   → manual override
 *   POST /draw-ops/event/:id/publish|unpublish  → draft ↔ live
 *
 * Nothing here mutates live event_registrations / signup state — the draw
 * lives entirely in draw_executions(placement_status='draft') +
 * draft_draw_entries until an explicit publish/finalize. Re-running is
 * deterministic given member weights; only non-member random placement
 * (and any newly added members) changes between runs.
 *
 * The member weight (priority_position, 1 = first pick) is internal — it
 * is shown to admins/delegates here but never to contestants.
 */

import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Shuffle,
  Loader2,
  AlertTriangle,
  Trophy,
  RotateCcw,
  GripVertical,
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  Lock,
  Unlock,
  CheckCircle2,
  Undo2,
  Send,
  Ban,
  Trash2,
  Info,
  Printer,
  MailX,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useCapacityConfig,
  useDraftDraw,
  useDrawConflicts,
  useExecutePlacement,
  useResetDraft,
  useUpdateDraftEntry,
  useReorderDraftPerformance,
  useReorderDraftPerformanceByWinnings,
  type DrawRunOrderMode,
  type WinningsReorderMode,
  usePublishDraw,
  useUnpublishDraw,
  useFinalizeDraw,
  useUnfinalizeDraw,
  useSendDrawNotifications,
  useDrawNotificationRecipients,
  useDrawNotificationTemplate,
  useDrawResultsVisibility,
  useSetDrawResultsVisibility,
  useDrawPublishPreview,
  useCancelDrawnOutEntries,
  useDrawnOutStatus,
  useDaySheet,
} from '@/api/extensions/useDrawOps';
import { PermissionGate } from '@/components/rbac/PermissionGate';
import { JudgeSheetPrintable } from '@/components/events/JudgeSheetPrintable';
import { resolveJudgeSheetLayout } from '@/lib/judge-sheet-templates';
import {
  useEventRegistrations,
  useAdminPairPartnerless,
  useAdminCancelPendingPartner,
  useAdminAcceptOnBehalfPartner,
  useAdminReassignPartner,
  useAdminSendPartnerReminder,
  useAdminSendCustomMessage,
  type AdminReassignPartnerData,
} from '@/api/extensions/useRegistrationOps';
import { useEvents } from '@/api/generated_hooks/generated-events-hooks';
import { isTeamRopingEvent } from '@/utils/eventTypes';
import { Switch } from '@/components/ui/switch';
import { DrawNotificationTemplateEditor } from '@/components/delegate/DrawNotificationTemplateEditor';
import { DrawNotificationHistoryPanel } from '@/components/delegate/DrawNotificationHistoryPanel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/contexts/ToastContext';
import { CancelDrawnOutModal } from '@/components/delegate/CancelDrawnOutModal';
import { InitializeDrawPanel } from '@/components/delegate/InitializeDrawPanel';
import { useDrawMode } from '@/api/extensions/useDrawInitialization';
import { formatPreferenceLabel } from '@/utils/format';

interface RodeoDrawManagementProps {
  eventId: string;
  eventName: string;
  rodeoId: string | null;
  onBack: () => void;
}

type PreferenceOutcome =
  | 'first'
  | 'second'
  | 'none'
  | 'draw_out'
  | 'unplaced'
  // Finals rodeo: every contestant runs every performance, so there are no
  // preferences to satisfy and no colour coding to apply.
  | 'finals';

// The backend enriches each draft entry (buildDraftExecution) with the
// contestant's name, member status, and preference outcome.
interface DraftEntryRow {
  id: string;
  registration_id: string;
  performance_id: string | null;
  draw_position: number;
  priority_position: number | null;
  is_placed: boolean;
  manual_override: boolean;
  unplaced_reason: string | null;
  display_name?: string;
  non_member?: boolean;
  buddy_group_id?: string | null;
  buddy_group_number?: number | null;
  first_performance_preference?: string | null;
  second_performance_preference?: string | null;
  is_draw_out?: boolean;
  preference_outcome?: PreferenceOutcome;
  is_team?: boolean;
  is_team_primary?: boolean;
  team_position?: string | null;
  team_partner_name?: string | null;
  team_partner_position?: string | null;
  // True when the underlying event_registrations row is cancelled —
  // typically because publish ran with cancel-unplaced and processed
  // their refund. Filtered out of the draw screen entirely (these rows
  // are not part of the draw and showing them with "needs placement"
  // labels was misleading); kept on the interface because the backend
  // still returns it and the filter above reads it.
  registration_cancelled?: boolean;
  // Set when this contestant still owes entry fees from a rodeo that has
  // already FINISHED (the backend's is_due_now outstanding-balance rows —
  // the same fees the registration gate enforces). Lets the admin/delegate
  // running this (upcoming) draw decide whether to keep them in or pull
  // them out via the move control. Null/absent when nothing is past-due.
  // Fees on still-upcoming rodeos never appear here.
  past_due_fees?: PastDueFees | null;
  // user_profiles.id for this contestant (and their team partner), so the
  // draw view can link names to the user-details page.
  user_profile_id?: string | null;
  team_partner_user_profile_id?: string | null;
}

interface PastDueFeeItem {
  event_id: string;
  event_name: string | null;
  organization_name: string | null;
  rodeo_name: string | null;
  rodeo_start_date: string | null;
  rodeo_end_date: string | null;
  amount_owed: number;
}
interface PastDueFees {
  total_owed: number;
  items: PastDueFeeItem[];
}

// Deterministic colour per buddy-group number so the same group is
// visually linkable across the lists within an event.
const BUDDY_COLORS = [
  'bg-sky-600',
  'bg-violet-600',
  'bg-pink-600',
  'bg-teal-600',
  'bg-indigo-600',
  'bg-fuchsia-600',
  'bg-cyan-600',
  'bg-rose-600',
];
function buddyColor(n: number): string {
  return BUDDY_COLORS[(n - 1) % BUDDY_COLORS.length];
}

const UNDRAWN = '__undrawn__';
const DRAWOUT = '__drawout__';

// Match a v4-shaped UUID anywhere in a string. Used to scrub performance
// UUIDs out of historical draw warnings (older drafts persisted the raw
// id before the backend was taught to render a human-readable label).
const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

// Replace any UUID substring in `warning` with the matching performance's
// name. Unknown UUIDs fall back to `Performance <short>` (same shape the
// backend uses) so the rendered text never leaks a raw id.
function humanizeWarningPerformanceIds(
  warning: string,
  perfs: Array<{ performance_id: string; performance_name: string | null }>,
): string {
  return warning.replace(UUID_RE, (uuid) => {
    const match = perfs.find((p) => p.performance_id === uuid);
    const name = match?.performance_name?.trim();
    if (name) return name;
    const short = uuid.split('-')[0] ?? uuid;
    return `Performance ${short}`;
  });
}

type FilterKey = 'all' | 'first' | 'second' | 'none' | 'draw_out';

function matchesFilter(e: DraftEntryRow, f: FilterKey): boolean {
  if (f === 'all') return true;
  // A "draw out" is one that was actually removed (1st pref was full) —
  // NOT merely someone who chose draw-out as a fallback but got placed.
  return e.preference_outcome === f;
}

/**
 * Compact relative-time formatter for partner-invite timestamps. Returns
 * strings like "12m", "4h", "2d" or "just now". No locale wrapping — the
 * UI uses it inside an admin-only panel where short tokens read faster
 * than a full Intl phrase.
 */
function formatInvitedAgo(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const diffMs = Date.now() - t;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  return `${wk}w ago`;
}

/** Cheap dollar formatter for fee previews. */
function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '$0.00';
  }
  return `$${value.toFixed(2)}`;
}

// Whole-dollar figure for the finals run-order suffix ("$1,234 YTD").
function formatWholeDollars(value: number | null | undefined): string {
  const n = Number(value);
  return `$${(Number.isFinite(n) ? Math.round(n) : 0).toLocaleString('en-US')}`;
}

// Finals run-order picker: the same five modes live scoring offers. Only the
// four money modes hit the backend; 'draw' just clears the last-applied
// marker on this screen (the draw is already whatever it is).
const RUN_ORDER_OPTIONS: ReadonlyArray<{ value: DrawRunOrderMode; label: string }> = [
  { value: 'draw', label: 'Draw order' },
  { value: 'ytd_desc', label: 'YTD high → low' },
  { value: 'ytd_asc', label: 'YTD low → high' },
  { value: 'current_desc', label: 'Current high → low' },
  { value: 'current_asc', label: 'Current low → high' },
];

function runOrderToastLabel(mode: WinningsReorderMode): string {
  const source = mode.startsWith('ytd') ? 'YTD winnings' : 'Current winnings';
  return `${source}, ${mode.endsWith('desc') ? 'high to low' : 'low to high'}`;
}

// Short date-range label for a rodeo, e.g. "Jun 1 – 3, 2025" collapses to a
// single date when start === end (or only one is known). Date-only strings
// are parsed as local midnight so the label never drifts a day in the
// browser's timezone.
function formatDateRangeShort(
  start?: string | null,
  end?: string | null,
): string {
  const fmt = (iso: string): string => {
    const d = new Date(iso.includes('T') ? iso : `${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };
  const s = start ? fmt(start) : '';
  const e = end ? fmt(end) : '';
  if (s && e && s !== e) return `${s} – ${e}`;
  return s || e || '';
}

// One-line label for a single owed item: "Event — Rodeo · Org · date range".
// Pieces that are missing are dropped so it never renders empty separators.
function pastDueItemLabel(i: PastDueFeeItem): string {
  const range = formatDateRangeShort(i.rodeo_start_date, i.rodeo_end_date);
  return [
    i.event_name ?? 'Event',
    i.rodeo_name,
    i.organization_name,
    range || null,
  ]
    .filter(Boolean)
    .join(' · ');
}

// Hover/native-tooltip text itemizing a contestant's unpaid fees from
// finished rodeos, so an admin can see exactly what's owed before deciding
// whether to keep them in the draw or move them out.
function pastDueTooltip(fees: PastDueFees): string {
  const lines = fees.items.map(
    (i) => `• ${pastDueItemLabel(i)}: ${formatMoney(i.amount_owed)}`,
  );
  return [
    `Owes ${formatMoney(fees.total_owed)} in unpaid fees from finished rodeo(s):`,
    ...lines,
    'Decide whether to keep them in the draw or move them out.',
  ].join('\n');
}

// Render a contestant's name as a link to their user-details page. Falls
// back to plain text when we don't have their user_profile_id. Stops click
// propagation so following the link never triggers row drag/move handlers.
function ContestantNameLink({
  name,
  userId,
  className,
}: {
  name: string | null | undefined;
  userId: string | null | undefined;
  className?: string;
}) {
  const label = name && name.length > 0 ? name : 'Contestant';
  if (!userId) return <>{label}</>;
  return (
    <Link
      to={`/users/${userId}`}
      className={className ?? 'hover:underline'}
      onClick={(ev) => ev.stopPropagation()}
    >
      {label}
    </Link>
  );
}

export default function RodeoDrawManagement({
  eventId,
  eventName,
  rodeoId,
  onBack,
}: RodeoDrawManagementProps) {
  const { showSuccess, showError } = useToast();

  // Rodeo draw mode (sandbox vs live) — gates the "This is a sandbox
  // draw" reassurance banner near the bottom of the screen. The
  // InitializeDrawPanel uses the same hook (React Query dedupes), so a
  // single network round-trip drives both.
  const { data: drawModeStatus } = useDrawMode(rodeoId);
  const isSandboxMode = drawModeStatus?.draw_mode === 'sandbox';

  const { data: capacity, isLoading: capLoading } = useCapacityConfig(eventId);
  const {
    data: draft,
    isLoading: draftLoading,
    isError: draftMissing,
    refetch: refetchDraft,
  } = useDraftDraw(eventId);
  const { data: conflicts } = useDrawConflicts(eventId);
  // Pull ALL event_registrations (not just draw-eligible ones) so we can
  // surface team-roping entries that exist but have no approved partner.
  // The draft draw filters those out (draw_eligible=false), so without
  // this side-channel the admin has no way to see David/Birch-style
  // partner-orphan rows on this page — they paid or registered but the
  // draw refuses to include them until a partner is on the row.
  const { data: regsResp } = useEventRegistrations(eventId);
  // Discipline gate. The partnerless panel ONLY applies to team-roping;
  // every other discipline (Tie-Down, Barrel Racing, Bareback, etc.)
  // has one contestant per entry, so EVERY row is `individual` with no
  // partner linkage — without this gate the legacy-orphan branch below
  // would flag the entire field as partnerless on every non-team event.
  const { data: drawEvent } = useEvents(eventId);
  const isTeamEvent = useMemo(
    () => isTeamRopingEvent(drawEvent as any),
    [drawEvent],
  );
  const partnerlessRegs: Array<{
    id: string;
    name: string;
    position: string | null;
    status: string | null;
    user_profile_id: string | null;
    admin_message_last_sent_at: string | null;
  }> = useMemo(() => {
    if (!isTeamEvent) return [];
    const list: any[] = Array.isArray(regsResp)
      ? regsResp
      : (regsResp as any)?.registrations ?? (regsResp as any)?.data ?? [];
    return list
      .filter((r: any) => {
        const statusName = (r.trig_status_name ?? r.trig_status ?? '').toLowerCase();
        if (statusName === 'cancelled') return false;
        const hasPartnerLink =
          !!r.partner_user_profile_id || !!r.partner_registration_id;
        const hasConfirmedPick =
          !!r.pick_partner_id && r.pick_confirmed === true;
        // Explicit flag set when a partner declines / cancels.
        if (r.partner_status === 'needs_partner') return true;
        // Legacy orphans: an individual or team_primary row with no partner
        // linkage and no confirmed pick. New entries can't create this shape
        // any more, but pre-fix data can.
        if (
          (r.registration_type === 'individual' ||
            r.registration_type === 'team_primary') &&
          !hasPartnerLink &&
          !hasConfirmedPick &&
          (r.partner_status === 'none' ||
            r.partner_status === 'cancelled' ||
            r.partner_status === 'rejected' ||
            r.partner_status == null)
        ) {
          return true;
        }
        return false;
      })
      .map((r: any) => ({
        id: r.id,
        name:
          [r.trig_first_name ?? r.first_name, r.trig_last_name ?? r.last_name]
            .filter(Boolean)
            .join(' ')
            .trim() ||
          r.trig_user_full_name ||
          '—',
        position: r.position ?? null,
        status: r.partner_status ?? null,
        user_profile_id: r.user_profile_id ?? null,
        admin_message_last_sent_at: r.admin_message_last_sent_at ?? null,
      }));
  }, [regsResp, isTeamEvent]);

  // Active (non-cancelled) team-roping rows on the event, with their pairing
  // state classified. Used to power the "Pair with…" dropdown, which now
  // surfaces EVERY opposite-position contestant — not just the partnerless
  // ones — so an admin can see who someone is currently paired with before
  // deciding whether to displace them. Compared to partnerlessRegs this is
  // a superset, with an `isPaired` flag + `partner_name` for the already-
  // paired bucket. We compute it client-side from the same regsResp payload
  // already in the cache; no new request.
  const allTeamRegs: Array<{
    id: string;
    name: string;
    position: string | null;
    user_profile_id: string | null;
    isPaired: boolean;
    partner_name: string | null;
    partner_registration_id: string | null;
    /**
     * Approval state — drives the fee preview in the dropdown:
     *  - 'approved': already in a confirmed team. Reassigning to them
     *    must create a NEW entry (do not break their confirmed team).
     *  - 'pending':  invitation sent but not yet accepted. Reassigning
     *    reuses their existing row, the other side of their pending
     *    team gets dropped.
     *  - 'unpaired': partnerless / needs_partner / cancelled-invite
     *    survivor. Reassigning reuses their row directly.
     */
    approval: 'approved' | 'pending' | 'unpaired';
    /** Per-side fee owed on their current row (null when unknown). */
    payment_amount: number | null;
    /** True when the seat owes money right now; false when fully covered. */
    payment_owed: boolean;
    /** True when the row's status is pending_payment / pending_confirmation_and_payment. */
    is_pending_payment: boolean;
  }> = useMemo(() => {
    if (!isTeamEvent) return [];
    const list: any[] = Array.isArray(regsResp)
      ? regsResp
      : (regsResp as any)?.registrations ?? (regsResp as any)?.data ?? [];
    const partnerlessIds = new Set(partnerlessRegs.map((r) => r.id));
    // Build a name lookup so we can label an already-paired row with its
    // current partner's name without a second pass through the array per row.
    const nameById = new Map<string, string>();
    for (const r of list) {
      const n =
        [r.trig_first_name ?? r.first_name, r.trig_last_name ?? r.last_name]
          .filter(Boolean)
          .join(' ')
          .trim() ||
        r.trig_user_full_name ||
        '';
      if (n) nameById.set(r.id, n);
    }
    return list
      .filter((r: any) => {
        const statusName = (r.trig_status_name ?? r.trig_status ?? '').toLowerCase();
        if (statusName === 'cancelled') return false;
        // Only Header / Heeler rows on this event participate in pairing.
        return r.position === 'Header' || r.position === 'Heeler';
      })
      .map((r: any) => {
        const isPaired = !partnerlessIds.has(r.id);
        const partnerRegId = r.partner_registration_id ?? null;
        const partnerName = partnerRegId
          ? nameById.get(partnerRegId) ?? null
          : null;
        const statusName = (r.trig_status_name ?? r.trig_status ?? '').toLowerCase();
        const partnerStatus = r.partner_status ?? null;
        const approval: 'approved' | 'pending' | 'unpaired' =
          partnerStatus === 'approved'
            ? 'approved'
            : partnerStatus === 'pending'
              ? 'pending'
              : 'unpaired';
        const paymentAmount =
          r.payment_amount === null || r.payment_amount === undefined
            ? null
            : Number(r.payment_amount);
        const isPendingPayment =
          statusName === 'pending_payment' ||
          statusName === 'pending_confirmation_and_payment';
        return {
          id: r.id,
          name:
            [r.trig_first_name ?? r.first_name, r.trig_last_name ?? r.last_name]
              .filter(Boolean)
              .join(' ')
              .trim() ||
            r.trig_user_full_name ||
            '—',
          position: r.position ?? null,
          user_profile_id: r.user_profile_id ?? null,
          isPaired,
          partner_name: partnerName,
          partner_registration_id: partnerRegId,
          approval,
          payment_amount: paymentAmount,
          payment_owed: isPendingPayment && (paymentAmount ?? 0) > 0,
          is_pending_payment: isPendingPayment,
        };
      });
  }, [regsResp, isTeamEvent, partnerlessRegs]);

  // Pending-approval teams: one row per team where the invitation has not
  // been accepted yet. Dedup by the (team_primary, team_partner) pair —
  // we only emit ONE entry per team, anchored on the team_primary row so
  // the Reassign control has stable identity. Each row carries both sides
  // (initiator + invitee) plus the invitation timestamp for the "invited
  // 4h ago" display. Used to power the new "Pending approval" panel.
  const pendingTeams: Array<{
    primary_registration_id: string;
    partner_registration_id: string;
    primary_name: string;
    partner_name: string;
    primary_position: string | null;
    partner_position: string | null;
    primary_user_profile_id: string | null;
    partner_user_profile_id: string | null;
    partner_invited_at: string | null;
    primary_payment_amount: number | null;
    partner_payment_amount: number | null;
    primary_pending_payment: boolean;
    partner_pending_payment: boolean;
    buddy_group_id: string | null;
    /** Timestamp from the team_partner row — null if no reminder sent yet. */
    partner_reminder_last_sent_at: string | null;
  }> = useMemo(() => {
    if (!isTeamEvent) return [];
    const list: any[] = Array.isArray(regsResp)
      ? regsResp
      : (regsResp as any)?.registrations ?? (regsResp as any)?.data ?? [];
    const byId = new Map<string, any>();
    for (const r of list) byId.set(r.id, r);
    const out: any[] = [];
    const seen = new Set<string>();
    for (const r of list) {
      const statusName = (r.trig_status_name ?? r.trig_status ?? '').toLowerCase();
      if (statusName === 'cancelled') continue;
      if (r.partner_status !== 'pending') continue;
      if (r.position !== 'Header' && r.position !== 'Heeler') continue;
      // Anchor on the team_primary row so we emit each team exactly once.
      // (Some pre-trigger rows may have null registration_type — fall back
      // to "has partner_invited_at column NULL → likely the primary" since
      // partner_invited_at lives on the team_partner row.)
      const isPrimary =
        r.registration_type === 'team_primary' ||
        (!r.registration_type && !r.partner_invited_at);
      if (!isPrimary) continue;
      if (!r.partner_registration_id) continue;
      const partnerRow = byId.get(r.partner_registration_id);
      if (!partnerRow) continue;
      const partnerStatusName = (partnerRow.trig_status_name ?? '').toLowerCase();
      if (partnerStatusName === 'cancelled') continue;
      const teamKey = [r.id, partnerRow.id].sort().join('|');
      if (seen.has(teamKey)) continue;
      seen.add(teamKey);

      const nameOf = (row: any) =>
        [row.trig_first_name ?? row.first_name, row.trig_last_name ?? row.last_name]
          .filter(Boolean)
          .join(' ')
          .trim() ||
        row.trig_user_full_name ||
        '—';
      const isPendingPay = (row: any) => {
        const s = (row.trig_status_name ?? row.trig_status ?? '').toLowerCase();
        return s === 'pending_payment' || s === 'pending_confirmation_and_payment';
      };
      out.push({
        primary_registration_id: r.id,
        partner_registration_id: partnerRow.id,
        primary_name: nameOf(r),
        partner_name: nameOf(partnerRow),
        primary_position: r.position ?? null,
        partner_position: partnerRow.position ?? null,
        primary_user_profile_id: r.user_profile_id ?? null,
        partner_user_profile_id: partnerRow.user_profile_id ?? null,
        partner_invited_at: partnerRow.partner_invited_at ?? null,
        primary_payment_amount:
          r.payment_amount === null || r.payment_amount === undefined
            ? null
            : Number(r.payment_amount),
        partner_payment_amount:
          partnerRow.payment_amount === null ||
          partnerRow.payment_amount === undefined
            ? null
            : Number(partnerRow.payment_amount),
        primary_pending_payment: isPendingPay(r),
        partner_pending_payment: isPendingPay(partnerRow),
        buddy_group_id: r.buddy_group_id ?? null,
        // partner_reminder_last_sent_at lives on the team_partner row
        // (the invitee — they're the one who receives the reminder).
        partner_reminder_last_sent_at:
          partnerRow.partner_reminder_last_sent_at ?? null,
      });
    }
    // Most-recently-invited first so the freshest teams are at the top.
    out.sort((a, b) => {
      const at = a.partner_invited_at ? Date.parse(a.partner_invited_at) : 0;
      const bt = b.partner_invited_at ? Date.parse(b.partner_invited_at) : 0;
      return bt - at;
    });
    return out;
  }, [regsResp, isTeamEvent]);

  const executePlacement = useExecutePlacement();
  const resetDraft = useResetDraft();
  const updateEntry = useUpdateDraftEntry();
  const reorderPerf = useReorderDraftPerformance();
  const reorderByWinnings = useReorderDraftPerformanceByWinnings();
  // Finals: the last winnings mode applied to each performance on THIS
  // screen, with the dollars each registration was ordered by, so the rows
  // can show "$1,234 YTD". Deliberately not persisted — the backend stores
  // only the resulting draw positions, so a reload shows plain draw order.
  const [winningsOrder, setWinningsOrder] = useState<
    Record<string, { mode: WinningsReorderMode; amounts: Record<string, number> }>
  >({});
  const clearWinningsOrder = (performanceId: string) =>
    setWinningsOrder((prev) => {
      if (!(performanceId in prev)) return prev;
      const next = { ...prev };
      delete next[performanceId];
      return next;
    });
  const publishDraw = usePublishDraw();
  const unpublishDraw = useUnpublishDraw();
  const finalizeDraw = useFinalizeDraw();
  const unfinalizeDraw = useUnfinalizeDraw();
  const cancelDrawnOut = useCancelDrawnOutEntries();
  const sendNotifications = useSendDrawNotifications();
  const pairPartnerless = useAdminPairPartnerless();
  const cancelPending = useAdminCancelPendingPartner();
  const acceptOnBehalf = useAdminAcceptOnBehalfPartner();
  const reassignPartner = useAdminReassignPartner();
  const sendPartnerReminder = useAdminSendPartnerReminder();
  const sendCustomMessage = useAdminSendCustomMessage();
  // Modal state for the "Send message" composer on partnerless rows.
  // Tracks which row was clicked + the in-progress subject / body so
  // the admin can refine the message before sending.
  const [messageModalFor, setMessageModalFor] = useState<
    | (typeof partnerlessRegs[number] & { event_name?: string | null })
    | null
  >(null);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  // Tracks which partnerless row the admin has opened the pair-with
  // dropdown for. Only one open at a time so the UI stays scannable.
  const [pairingOpenFor, setPairingOpenFor] = useState<string | null>(null);
  // Per-pending-team state for the inline "Reassign" control: which team's
  // reassign panel is open, which side is being kept, and confirmation
  // dialog payload. Only one team's panel may be open at a time.
  const [reassignOpenFor, setReassignOpenFor] = useState<string | null>(null);
  const [reassignKeepSide, setReassignKeepSide] = useState<'primary' | 'partner'>('primary');
  type ReassignConfirmState = {
    team: typeof pendingTeams[number];
    keepSide: 'primary' | 'partner';
    candidate:
      | {
          mode: 'reuse';
          registration_id: string;
          name: string;
          position: string | null;
          approval: 'pending' | 'unpaired';
          payment_amount: number | null;
          payment_owed: boolean;
          /** Other-side info when candidate is in a pending team. */
          displaced_name: string | null;
        }
      | {
          mode: 'new_entry';
          user_profile_id: string;
          name: string;
          position: 'Header' | 'Heeler';
          existing_entry_payment: number | null;
          new_entry_fee: number;
        };
    fee_mode?: 'normal' | 'free';
  };
  const [reassignConfirm, setReassignConfirm] = useState<ReassignConfirmState | null>(null);
  // Per-pending-team cancel confirmation. Dialog displays the two affected
  // contestants so the admin can verify before pulling the trigger.
  const [cancelConfirmFor, setCancelConfirmFor] = useState<
    | typeof pendingTeams[number]
    | null
  >(null);
  // Per-pending-team accept-on-behalf confirmation.
  const [acceptConfirmFor, setAcceptConfirmFor] = useState<
    | typeof pendingTeams[number]
    | null
  >(null);
  // Pull the template lazily so the editor's GET is cache-shared with the
  // header (which surfaces "last sent: …" in the Send dialog copy). The
  // editor itself calls the same hook — React Query dedupes.
  const { data: notificationTemplate } = useDrawNotificationTemplate(eventId);
  const { data: resultsVisibility } = useDrawResultsVisibility(eventId);
  const setResultsVisibility = useSetDrawResultsVisibility();
  // Cheap per-event drawn-out status; gates the Cancel Drawn-Out
  // button + populates the count badge. Polled when the page is open.
  const { data: drawnOutStatus } = useDrawnOutStatus(eventId);

  const [filter, setFilter] = useState<FilterKey>('all');
  // Confirmation dialogs (finalize, unfinalize, send, cancel-drawn-out).
  // Kept as separate flags because the copy + destructive-styling
  // differs and one-flag-with-mode would just be branching inside the
  // JSX for no readability win.
  const [cancelDrawnOutOpen, setCancelDrawnOutOpen] = useState(false);
  // Lazy preview hook — only fires when the cancel-drawn-out modal
  // opens. Shared with the modal so the admin sees current state
  // (someone could have been placed since last check).
  const drawnOutPreview = useDrawPublishPreview(eventId, cancelDrawnOutOpen);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [unfinalizeOpen, setUnfinalizeOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  // Send-confirmation recipient list (one row per entry) + the set of
  // entries the admin has toggled OFF. Empty = all selected (the default),
  // so newly-loaded recipients start enabled with no effect needed. Reset
  // whenever the modal opens so each send starts from "all".
  const { data: recipientsData, isLoading: recipientsLoading } =
    useDrawNotificationRecipients(eventId, sendOpen);
  const recipients = recipientsData?.recipients ?? [];
  const [deselectedRecipients, setDeselectedRecipients] = useState<Set<string>>(
    new Set(),
  );
  const selectedRecipientIds = recipients
    .map((r) => r.event_registration_id)
    .filter((id) => !deselectedRecipients.has(id));
  const allRecipientsSelected =
    recipients.length > 0 && selectedRecipientIds.length === recipients.length;
  const openSendModal = () => {
    setDeselectedRecipients(new Set());
    setSendOpen(true);
  };
  const toggleRecipient = (id: string) => {
    setDeselectedRecipients((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllRecipients = () => {
    setDeselectedRecipients(
      allRecipientsSelected
        ? new Set(recipients.map((r) => r.event_registration_id))
        : new Set(),
    );
  };
  // Template editor and send-history are independent opt-in panels —
  // showing one must not force the other open. Keeps the draw screen
  // header clean for the common "I just want to lock/finalize" path.
  // Persisted across re-renders but not across navigations — by design.
  const [templateOpen, setTemplateOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const perfs = capacity?.performances ?? [];
  const namedPerfs = perfs.filter((p) => !p.is_slack);
  const slackPerfs = perfs.filter((p) => p.is_slack);
  // Finals rodeo: the draw seats every contestant in every performance the
  // event's rounds are tied to, so each section is labelled by its round and
  // the preference legend / filters are dropped — there are no preferences.
  const isFinals = capacity?.is_finals === true;
  const perfTitle = (p: { performance_name: string; round_label?: string | null }) =>
    isFinals && p.round_label
      ? `${p.round_label} — ${p.performance_name}`
      : p.performance_name;
  const allEntries = (draft?.entries ?? []) as unknown as DraftEntryRow[];
  // Cancelled registrations (refunded at publish) are excluded from every
  // count, group, filter, and list on this screen — they're not part of
  // the draw, so showing them with "needs placement" / "refund issued"
  // / "locked - refunded at publish" labels was actively misleading.
  // `hasDraft` still keys off the unfiltered count so a published draw
  // that happens to be 100% cancelled doesn't flip back to the empty
  // "No draw yet" state.
  const entries = useMemo(
    () => allEntries.filter((e) => e.registration_cancelled !== true),
    [allEntries],
  );
  // ONE entry, not one row. On a finals rodeo a single entry owns one row per
  // performance, so `entries.length` counts a contestant once per round —
  // de-duplicate by registration_id so "Total entries" stays a contestant
  // count. Identical to entries.length on a regular rodeo.
  const distinctEntryCount = useMemo(
    () => new Set(entries.map((e) => e.registration_id)).size,
    [entries],
  );
  // Contestants in this draw who still owe fees from a FINISHED rodeo
  // (flagged by the backend's past_due_fees). Deduped per person — a
  // contestant with several entries carries the same total on each row, so
  // we key on name + total to collapse them into a single summary line.
  const feeOwingContestants = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{
      name: string;
      userId: string | null;
      fees: PastDueFees;
    }> = [];
    for (const e of entries) {
      const fees = e.past_due_fees;
      if (!fees || fees.total_owed <= 0) continue;
      const name = e.display_name ?? 'Contestant';
      const userId = e.user_profile_id ?? null;
      const key = `${userId ?? name}|${fees.total_owed.toFixed(2)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ name, userId, fees });
    }
    return out;
  }, [entries]);
  const hasDraft = !draftMissing && allEntries.length > 0;
  const placementStatus = draft?.placement_status ?? 'none';
  const isPublished = placementStatus === 'published';
  const isFinalized = placementStatus === 'finalized';

  // Day-sheet enrichment (rodeo_name, organization_name, event_date, and
  // the resolved judge_sheet_columns / min_rows) is the same regardless of
  // publish state — the endpoint returns the enrichment block for any
  // event that exists. We use it to render the printable header + columns
  // for the Print Judge Sheet button below; contestants themselves come
  // from the local draft entries, not from this query.
  const { data: daySheet } = useDaySheet(eventId);
  const judgeSheetLayout = useMemo(
    () =>
      resolveJudgeSheetLayout({
        columns: (daySheet as any)?.judge_sheet_columns,
        minRows: (daySheet as any)?.judge_sheet_min_rows,
        templateSlug: (daySheet as any)?.judge_sheet_template,
        eventTypeName: (daySheet as any)?.event_type_name,
      }),
    [daySheet],
  );
  const judgeSheetEventDateLabel = useMemo(() => {
    const raw = (daySheet as any)?.event_date;
    if (!raw) return '';
    return new Date(raw).toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [daySheet]);

  // The per-performance judge-sheet projection (`judgeSheetPerformances`) is
  // built below, after the `byPerf` grouping it depends on. `canPrintJudgeSheet`
  // only needs to know whether there is anything at all to print.
  const canPrintJudgeSheet = hasDraft && entries.length > 0;

  function handlePrintDraftJudgeSheet() {
    // Same flow as DaySheetView: the JudgeSheetPrintable component portals
    // into document.body under a class the print stylesheet whitelists, so
    // calling window.print() fires the print preview with the sheet as the
    // only visible content.
    if (!canPrintJudgeSheet) return;
    window.print();
  }
  // Once published OR finalized, the draft is no longer editable: the
  // entries that the daysheet was written from must match the live draw.
  const isLocked = isPublished || isFinalized;

  // Per-seat fee charged for a NEW entry created by Reassign's
  // create-new-entry path (the picked partner is already in an approved
  // team and is being added as a second entry to fill a performance).
  // Numeric coerce here because event_registrations stores fees as
  // numeric/string in some paths.
  const eventEntryFee = useMemo(() => {
    const fee = (drawEvent as any)?.entry_fee;
    const parsed = parseFloat(String(fee ?? '0'));
    return Number.isFinite(parsed) ? parsed : 0;
  }, [drawEvent]);

  // Group entries. ANY draw-out is pulled into its own bucket and is
  // NEVER shown under a performance/slack/not-drawn list — a draw-out
  // contestant is, by definition, not running in a performance. Everyone
  // else: placed → their performance; otherwise → undrawn. (Cancelled
  // registrations are filtered out of `entries` above and never reach
  // this grouping pass.)
  const byPerf = useMemo(() => {
    const groups = new Map<string, DraftEntryRow[]>();
    for (const e of entries) {
      // Drive draw-out off the OUTCOME, never the draw-out *choice*.
      // preference_outcome === 'draw_out' ⇒ actually removed (1st pref
      // was full). Someone who chose draw-out but got their 1st pref has
      // outcome 'first' and is a normal contestant in that performance.
      const removedDrawOut = e.preference_outcome === 'draw_out';
      const notInPerf = !e.is_placed || !e.performance_id;
      const key: string = removedDrawOut
        ? DRAWOUT
        : notInPerf
          ? UNDRAWN
          : (e.performance_id as string);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(e);
    }
    for (const list of groups.values()) {
      list.sort((a, b) => a.draw_position - b.draw_position);
    }
    return groups;
  }, [entries]);

  // Draw-outs get their OWN top section — they are not performing in any
  // performance (their 2nd pref of "Draw Out" means: 1st pref or removed).
  const drawOuts = byPerf.get(DRAWOUT) ?? [];
  const undrawn = byPerf.get(UNDRAWN) ?? [];

  // Per-performance judge-sheet projection. The user's spec: break the printed
  // sheet apart so contestants are listed under their individual performance,
  // each on its own page, in the order they appear in that performance. We
  // reuse the existing `byPerf` grouping (already sorted by draw_position) and
  // walk performances in capacity order (named, then slack). Performances with
  // no contestants are skipped so we don't emit blank pages. Entries not
  // running in any performance (not yet drawn / drawn out) print on clearly
  // labelled trailing sheets so the secretary still sees them.
  const judgeSheetPerformances = useMemo(() => {
    const toContestants = (list: DraftEntryRow[]) =>
      list.map((e, idx) => {
        // Team roping: one slot per team. The draft entry is the primary;
        // `team_position` is the primary's role, `team_partner_name` the
        // other half. Resolve header/heeler so the sheet lists both.
        if (e.is_team) {
          const primaryIsHeader = e.team_position !== 'Heeler';
          const header = primaryIsHeader ? e.display_name : e.team_partner_name;
          const heeler = primaryIsHeader ? e.team_partner_name : e.display_name;
          return {
            position: e.draw_position ?? idx + 1,
            name: header ?? '',
            partnerName: heeler ?? '',
          };
        }
        return {
          position: e.draw_position ?? idx + 1,
          name: e.display_name ?? '',
        };
      });

    const fmtPerfDateTime = (iso?: string | null) => {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    };

    const out: {
      id: string;
      name: string;
      dateTime: string;
      contestants: { position: number; name: string; partnerName?: string }[];
    }[] = [];

    for (const p of [...namedPerfs, ...slackPerfs]) {
      const list = byPerf.get(p.performance_id) ?? [];
      if (list.length === 0) continue;
      out.push({
        id: p.performance_id,
        name: `${p.performance_name ?? 'Performance'}${p.is_slack ? ' (Slack)' : ''}`,
        dateTime: fmtPerfDateTime(p.start_datetime),
        contestants: toContestants(list),
      });
    }

    if (undrawn.length > 0) {
      out.push({ id: UNDRAWN, name: 'Not Yet Drawn', dateTime: '', contestants: toContestants(undrawn) });
    }
    if (drawOuts.length > 0) {
      out.push({ id: DRAWOUT, name: 'Drawn Out', dateTime: '', contestants: toContestants(drawOuts) });
    }

    return out;
  }, [namedPerfs, slackPerfs, byPerf, undrawn, drawOuts]);

  const counts = useMemo(() => {
    const c = { all: entries.length, first: 0, second: 0, none: 0, draw_out: 0 };
    for (const e of entries) {
      if (e.preference_outcome === 'first') c.first++;
      else if (e.preference_outcome === 'second') c.second++;
      else if (e.preference_outcome === 'none') c.none++;
      else if (e.preference_outcome === 'draw_out') c.draw_out++;
    }
    return c;
  }, [entries]);

  const handleGenerate = async () => {
    try {
      await executePlacement.mutateAsync(eventId);
      // A fresh draw is draw order again — drop every last-applied marker.
      setWinningsOrder({});
      await refetchDraft();
      showSuccess(
        hasDraft ? 'Draw re-run' : 'Draft draw generated',
        'Non-destructive — live signups are untouched until you lock the draw.',
      );
    } catch (e: unknown) {
      showError(
        'Draw generation failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  // Reset-only path: clears the current draft execution and returns the
  // event to a blank/unstarted draw state WITHOUT regenerating. Distinct
  // from handleGenerate (which is reset-then-redraw). Backend route is
  // idempotent — running with no draft is a no-op.
  const handleResetConfirm = async () => {
    try {
      const result = await resetDraft.mutateAsync(eventId);
      setResetOpen(false);
      setWinningsOrder({});
      await refetchDraft();
      showSuccess(
        result?.deleted ? 'Draw reset' : 'Nothing to reset',
        result?.deleted
          ? 'All draft entries removed. The draw is back to a blank state.'
          : 'There was no draft to clear.',
      );
    } catch (e: unknown) {
      showError(
        'Reset failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  const handleMove = async (entry: DraftEntryRow, targetPerfId: string) => {
    try {
      await updateEntry.mutateAsync(
        targetPerfId === UNDRAWN
          ? {
              eventId,
              entryId: entry.id,
              // explicit null + is_placed:false — undefined would be
              // stripped from the JSON body and the entry would not move.
              performance_id: null,
              is_placed: false,
            }
          : { eventId, entryId: entry.id, performance_id: targetPerfId },
      );
      await refetchDraft();
      showSuccess(
        targetPerfId === UNDRAWN ? 'Removed from draw' : 'Entry moved',
      );
    } catch (e: unknown) {
      showError(
        'Move failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  const handleReorder = async (
    performanceId: string,
    entryIds: string[],
  ) => {
    try {
      await reorderPerf.mutateAsync({ eventId, performanceId, entryIds });
      // A hand edit means the list is no longer "ordered by winnings".
      clearWinningsOrder(performanceId);
      await refetchDraft();
      showSuccess('Order updated');
    } catch (e: unknown) {
      showError(
        'Reorder failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  // Finals only. One-shot: the backend rewrites the performance's draw
  // positions by the chosen winnings figure and marks the rows manual
  // (same as a drag). 'draw' is a client-side no-op that clears the marker.
  const handleRunOrderChange = async (
    p: { performance_id: string; performance_name: string; round_label?: string | null },
    mode: DrawRunOrderMode,
  ) => {
    if (mode === 'draw') {
      clearWinningsOrder(p.performance_id);
      return;
    }
    try {
      const result = await reorderByWinnings.mutateAsync({
        eventId,
        performanceId: p.performance_id,
        mode,
      });
      setWinningsOrder((prev) => ({
        ...prev,
        [p.performance_id]: { mode, amounts: result.amounts ?? {} },
      }));
      await refetchDraft();
      showSuccess(
        `${p.round_label ?? p.performance_name} reordered by ${runOrderToastLabel(mode)}`,
      );
    } catch (e: unknown) {
      showError(
        'Run order failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  // Publish is now single-phase — it never cancels drawn-out
  // contestants automatically. Those have to be explicitly cancelled
  // via the separate "Cancel Drawn-Out Entries" action (see
  // handleCancelDrawnOutConfirm), typically after the org's 24-hour
  // call-in window closes. Unpublish is also single-phase.
  const handlePublishToggle = async () => {
    if (isPublished) {
      try {
        await unpublishDraw.mutateAsync(eventId);
        showSuccess('Draw unlocked', 'Reverted to draft — edits are allowed again.');
        await refetchDraft();
      } catch (e: unknown) {
        showError(
          'Lock change failed',
          e instanceof Error ? e.message : 'Unknown error',
        );
      }
      return;
    }

    try {
      await publishDraw.mutateAsync({ eventId });
      showSuccess(
        'Draw locked',
        'The draw is locked down so it can’t be changed. Drawn-out entries are still active — use Cancel Drawn-Out Entries when you’re ready to refund them.',
      );
      await refetchDraft();
    } catch (e: unknown) {
      showError(
        'Lock change failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  // Confirm handler for the Cancel Drawn-Out Entries modal. Destructive:
  // cancels every unplaced/drawn-out registration with refunds. Re-runs
  // are idempotent (cancelled_count = 0) so accidental double-clicks
  // don't double-charge anything.
  const handleCancelDrawnOutConfirm = async (
    refundFeeRegistrationIds: string[],
  ) => {
    try {
      const result = await cancelDrawnOut.mutateAsync({
        eventId,
        refundFeeRegistrationIds,
      });
      const cancelledCount = result?.cancelled_count ?? 0;
      setCancelDrawnOutOpen(false);
      await refetchDraft();
      showSuccess(
        cancelledCount > 0 ? 'Drawn-out entries cancelled' : 'Nothing to cancel',
        cancelledCount > 0
          ? `${cancelledCount} drawn-out contestant${cancelledCount === 1 ? '' : 's'} cancelled with refund.`
          : 'No drawn-out entries needed cancelling.',
      );
    } catch (e: unknown) {
      showError(
        'Cancel failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  const handleCancelDrawnOutClose = () => {
    if (cancelDrawnOut.isPending) return; // don't close mid-flight
    setCancelDrawnOutOpen(false);
  };

  const handleFinalizeConfirm = async () => {
    setFinalizeOpen(false);
    try {
      await finalizeDraw.mutateAsync(eventId);
      await refetchDraft();
      showSuccess(
        'Draw finalized',
        'Locked in. Notifications and live results will hang off this state.',
      );
    } catch (e: unknown) {
      showError(
        'Finalize failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  const handleUnfinalizeConfirm = async () => {
    setUnfinalizeOpen(false);
    try {
      await unfinalizeDraw.mutateAsync(eventId);
      await refetchDraft();
      showSuccess(
        'Draw unfinalized',
        'Reverted to locked — you can adjust before re-finalizing.',
      );
    } catch (e: unknown) {
      showError(
        'Unfinalize failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  const handleToggleResultsVisibility = async (visible: boolean) => {
    try {
      await setResultsVisibility.mutateAsync({ eventId, visible });
      showSuccess(
        visible ? 'Draw results visible' : 'Draw results hidden',
        visible
          ? 'Contestants can now see the per-performance draw on this event.'
          : 'The public draw-results card is hidden from contestants.',
      );
    } catch (e: unknown) {
      showError(
        'Visibility change failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  const handleSendNotificationsConfirm = async () => {
    setSendOpen(false);
    try {
      // Header Send button always uses the saved org template. The
      // editor's "Send with edits (one-off)" button handles the
      // override path independently — see DrawNotificationTemplateEditor.
      // recipient_ids carries the admin's per-entry selection; omitted only
      // when the recipient list never loaded (then the backend sends all).
      const result = await sendNotifications.mutateAsync({
        eventId,
        recipient_ids: recipients.length > 0 ? selectedRecipientIds : undefined,
      });
      const summary = [
        `${result.inapp_sent} in-app`,
        `${result.email_sent} email`,
        ...(result.email_skipped_optout > 0 ? [`${result.email_skipped_optout} opted out`] : []),
        ...(result.email_failed > 0 ? [`${result.email_failed} failed`] : []),
      ].join(' · ');
      showSuccess('Notifications sent', summary);
    } catch (e: unknown) {
      showError(
        'Send failed',
        e instanceof Error ? e.message : 'Unknown error',
      );
    }
  };

  const conflictCount = conflicts?.conflicts?.length ?? 0;
  // A draw-out who got their 1st preference IS performing, so it counts.
  const placedCount = entries.filter(
    (e) => e.is_placed && e.performance_id,
  ).length;
  const slackCount = entries.filter(
    (e) =>
      e.is_placed &&
      e.performance_id &&
      slackPerfs.some((p) => p.performance_id === e.performance_id),
  ).length;
  const busy =
    executePlacement.isPending ||
    resetDraft.isPending ||
    publishDraw.isPending ||
    unpublishDraw.isPending ||
    finalizeDraw.isPending ||
    unfinalizeDraw.isPending ||
    cancelDrawnOut.isPending ||
    sendNotifications.isPending;

  // The Cancel button surfaces after finalize when there is ANY
  // cancellable row left — drawn-out OR partnerless team-roping. Backend
  // now unions both cohorts behind `has_uncancelled` + `count`, and
  // breaks them down via `drawn_out_count` / `partnerless_count` so the
  // button label can adapt. The status endpoint is the single source of
  // truth — it powers both this gate and the admin dashboard warning
  // card so the two never diverge.
  const drawnOutCount = drawnOutStatus?.count ?? 0;
  const partnerlessCount = drawnOutStatus?.partnerless_count ?? 0;
  const onlyDrawnOutCount = drawnOutStatus?.drawn_out_count ?? 0;
  const canCancelDrawnOut =
    isFinalized && !!drawnOutStatus?.has_uncancelled && drawnOutCount > 0;
  // Adaptive button label. One button always, but its copy reflects what
  // it would actually cancel right now — the research card explicitly
  // recommended ONE button whose preview adapts.
  const cancelButtonLabel =
    onlyDrawnOutCount > 0 && partnerlessCount > 0
      ? 'Cancel drawn-out + partnerless'
      : partnerlessCount > 0
        ? 'Cancel partnerless'
        : 'Cancel drawn-out';
  const cancelButtonHoverTitle = !canCancelDrawnOut
    ? 'No drawn-out or partnerless entries pending cancellation'
    : `Cancel ${drawnOutCount} ${drawnOutCount === 1 ? 'entry' : 'entries'} (${onlyDrawnOutCount} drawn-out · ${partnerlessCount} partnerless) with refunds`;

  return (
    <div className="space-y-5">
      {/* Sandbox / live mode panel — gates publish/finalize/notify on
          the one-time ferris-wheel initialization. Only rendered when
          we know which rodeo the event belongs to. */}
      {rodeoId ? <InitializeDrawPanel rodeoId={rodeoId} /> : null}
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" onClick={onBack} className="mb-2 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Manage Draws
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            {eventName}
            <Badge
              variant={
                isFinalized
                  ? 'default'
                  : isPublished
                    ? 'default'
                    : placementStatus === 'draft'
                      ? 'secondary'
                      : 'outline'
              }
              className={
                isFinalized
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : isPublished
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : undefined
              }
            >
              {isFinalized
                ? 'Finalized'
                : isPublished
                  ? 'Locked'
                  : placementStatus === 'draft'
                    ? 'Draft'
                    : 'No draw'}
            </Badge>
          </h1>
          {((daySheet as any)?.organization_name ||
            (daySheet as any)?.rodeo_name) && (
            <p className="text-sm font-medium text-foreground mt-1">
              {[
                (daySheet as any)?.organization_name,
                (daySheet as any)?.rodeo_name,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            Rodeo draw · ferris-wheel weighted · non-destructive until locked
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Generate / Reset & re-run is a destructive draft operation —
              once a draw is locked or finalized, hide it entirely so the
              admin can't accidentally blow away a live draw. They must
              unlock/unfinalize first if they really need to start over. */}
          {!isLocked && (
            <Button onClick={handleGenerate} disabled={busy}>
              {executePlacement.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : hasDraft ? (
                <RotateCcw className="h-4 w-4 mr-1.5" />
              ) : (
                <Shuffle className="h-4 w-4 mr-1.5" />
              )}
              {hasDraft ? 'Reset & re-run' : 'Generate draft draw'}
            </Button>
          )}
          {/* Reset is gated on the EXISTENCE of a draft execution, not on
              entries.length. A draw can produce an execution row with zero
              placed entries (rough-stock event with no signups, every entry
              routed to slack/draw-out, etc.) — the conflict aggregator
              still emits the "zero entries for scored event" warning for
              that execution, so the admin needs a way to clear it from
              this screen. Backend POST /draft/reset is idempotent and
              tears down both the draw_executions row and any
              draft_draw_entries hanging off it. */}
          {placementStatus === 'draft' && (
            <Button
              variant="destructive"
              onClick={() => setResetOpen(true)}
              disabled={busy}
              title="Clear the draft execution and return to a blank state"
            >
              {resetDraft.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-1.5" />
              )}
              Reset
            </Button>
          )}
          {hasDraft && !isFinalized && (
            <PermissionGate permission="draws:publish">
              <Button
                variant={isPublished ? 'outline' : 'default'}
                onClick={handlePublishToggle}
                disabled={busy}
              >
                {isPublished ? (
                  <>
                    <Unlock className="h-4 w-4 mr-1.5" /> Unlock draw
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-1.5" /> Lock draw
                  </>
                )}
              </Button>
            </PermissionGate>
          )}

          {/* Pre-publish (and post-publish) Print Judge Sheet — uses the
              current draft entries plus the day-sheet enrichment block
              for headers/columns. Lets the secretary print a working
              sheet before everything is finalized; drawn-out / unplaced
              rows are annotated inline. Same window.print() flow as
              DaySheetView; the hidden JudgeSheetPrintable below renders
              the actual page. */}
          {canPrintJudgeSheet && (
            <PermissionGate permission="draws:print_judge_sheet">
              <Button
                variant="outline"
                onClick={handlePrintDraftJudgeSheet}
                disabled={busy}
                title="Print the judge sheet using the current draft entries"
              >
                <Printer className="h-4 w-4 mr-1.5" />
                Print judge sheet
              </Button>
            </PermissionGate>
          )}
          {hasDraft && isPublished && !isFinalized && (
            <Button
              variant="default"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => setFinalizeOpen(true)}
              disabled={busy}
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Finalize
            </Button>
          )}
          {hasDraft && isFinalized && (
            <Button
              variant="outline"
              onClick={() => setUnfinalizeOpen(true)}
              disabled={busy}
            >
              <Undo2 className="h-4 w-4 mr-1.5" /> Unfinalize
            </Button>
          )}
          {hasDraft && isFinalized && (
            <Button
              variant="destructive"
              onClick={() => setCancelDrawnOutOpen(true)}
              disabled={busy || !canCancelDrawnOut}
              title={cancelButtonHoverTitle}
            >
              <Ban className="h-4 w-4 mr-1.5" />
              {cancelButtonLabel}
              {canCancelDrawnOut && (
                <Badge
                  variant="secondary"
                  className="ml-2 bg-white/20 text-white border-white/30"
                >
                  {drawnOutCount}
                </Badge>
              )}
            </Button>
          )}
          {hasDraft && isFinalized && (
            <Button
              variant="default"
              onClick={openSendModal}
              disabled={busy}
            >
              {sendNotifications.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1.5" />
              )}
              Send notifications
            </Button>
          )}
          {hasDraft && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setTemplateOpen((v) => !v)}
            >
              {templateOpen ? 'Hide template' : 'Edit template'}
            </Button>
          )}
          {hasDraft && isFinalized && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setHistoryOpen((v) => !v)}
            >
              {historyOpen ? 'Hide history' : 'Show history'}
            </Button>
          )}
        </div>
      </div>

      {hasDraft && (
        <div className="rounded-md border bg-card px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Switch
              id="draw-results-visible"
              // The toggle only goes ON for a FINALIZED draw — public
              // results are gated on finalize state. Force-off + disabled
              // here so admins can't tee up a toggle that the backend
              // would reject; the auto-clear in the unfinalize route
              // keeps the DB value in sync if state moves back from
              // finalized.
              checked={isFinalized && !!resultsVisibility?.visible}
              onCheckedChange={handleToggleResultsVisibility}
              disabled={!isFinalized || setResultsVisibility.isPending}
            />
            <label
              htmlFor="draw-results-visible"
              className={`text-sm ${isFinalized ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
            >
              <span className="font-medium">Show draw results to contestants</span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                {isFinalized ? (
                  <>
                    When on, a "Draw Results" card appears on the event
                    detail page that every signed-in contestant can see.
                  </>
                ) : (
                  <>Finalize the draw to enable this toggle.</>
                )}
              </span>
            </label>
          </div>
          {isFinalized && resultsVisibility?.updated_at && (
            <div className="text-[11px] text-muted-foreground">
              Last applied:{' '}
              <time dateTime={resultsVisibility.updated_at}>
                {new Date(resultsVisibility.updated_at).toLocaleString()}
              </time>
              {resultsVisibility.updated_by_name && (
                <> by {resultsVisibility.updated_by_name}</>
              )}
            </div>
          )}
        </div>
      )}

      {/* Inline "notifications previously sent" notice. Surfaces the
          most-recent send timestamp + recipient count without making
          the admin open the Send Notifications modal to see it. Only
          shows when the draw has been finalized AND notifications have
          been sent at least once for this event's saved template. The
          full send history (per-pass, with resend / failed counts) is
          one click away via "Edit template". */}
      {hasDraft && isFinalized && notificationTemplate?.last_sent_at && (
        <div className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 flex items-center justify-between gap-3 flex-wrap dark:border-sky-900/60 dark:bg-sky-950/30">
          <div className="flex items-center gap-2 text-sm text-sky-900 dark:text-sky-100">
            <Send className="h-4 w-4 flex-shrink-0 text-sky-700 dark:text-sky-300" />
            <span>
              <span className="font-medium">Notifications previously sent.</span>{' '}
              Last sent{' '}
              <time dateTime={notificationTemplate.last_sent_at}>
                {new Date(notificationTemplate.last_sent_at).toLocaleString()}
              </time>
              {typeof notificationTemplate.last_sent_count === 'number' &&
                ` to ${notificationTemplate.last_sent_count} recipient${notificationTemplate.last_sent_count === 1 ? '' : 's'}`}
              . Sending again is allowed.
            </span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-sky-900 hover:bg-sky-100 dark:text-sky-100 dark:hover:bg-sky-900/40"
            onClick={() => setHistoryOpen((v) => !v)}
          >
            {historyOpen ? 'Hide history' : 'View history'}
          </Button>
        </div>
      )}

      {templateOpen && hasDraft && (
        <div className="rounded-lg border bg-card p-4">
          <DrawNotificationTemplateEditor
            eventId={eventId}
            canSend={isFinalized}
          />
        </div>
      )}

      {historyOpen && hasDraft && (
        <div className="rounded-lg border bg-card p-4">
          <DrawNotificationHistoryPanel
            eventId={eventId}
            canSend={isFinalized}
          />
        </div>
      )}

      <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalize this draw?</DialogTitle>
            <DialogDescription>
              Finalizing commits the locked draw and marks it ready for
              contestant notifications and the live-results tab. Existing
              day-sheet entries are not changed — finalize is non-destructive.
              You can unfinalize before scoring starts.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setFinalizeOpen(false)}
              disabled={finalizeDraw.isPending}
            >
              Cancel
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleFinalizeConfirm}
              disabled={finalizeDraw.isPending}
            >
              {finalizeDraw.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
              )}
              Finalize draw
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={resetOpen}
        onOpenChange={(open) => {
          if (!resetDraft.isPending) setResetOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset this draw?</DialogTitle>
            <DialogDescription>
              <span className="font-semibold text-destructive">
                This will remove all entries
              </span>{' '}
              from the current draft and return the event to a blank,
              unstarted state. Live signups and locked draws are not
              affected — only the in-progress draft is cleared. Use this
              when you want to start over without immediately generating
              a new draw. You can run <span className="font-semibold">Generate draft draw</span> again afterwards.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetOpen(false)}
              disabled={resetDraft.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetConfirm}
              disabled={resetDraft.isPending}
            >
              {resetDraft.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-1.5" />
              )}
              Reset draw
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unfinalizeOpen} onOpenChange={setUnfinalizeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unfinalize this draw?</DialogTitle>
            <DialogDescription>
              Reverts to the Locked state so you can adjust the draw or
              unlock entirely. Use this only before scoring starts —
              contestants may already have been notified.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUnfinalizeOpen(false)}
              disabled={unfinalizeDraw.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleUnfinalizeConfirm}
              disabled={unfinalizeDraw.isPending}
            >
              {unfinalizeDraw.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Undo2 className="h-4 w-4 mr-1.5" />
              )}
              Unfinalize
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CancelDrawnOutModal
        open={cancelDrawnOutOpen}
        onClose={handleCancelDrawnOutClose}
        onConfirm={handleCancelDrawnOutConfirm}
        preview={drawnOutPreview.data}
        isLoadingPreview={cancelDrawnOutOpen && drawnOutPreview.isLoading}
        isCancelling={cancelDrawnOut.isPending}
      />

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Send notifications</DialogTitle>
            <DialogDescription>
              Each selected contestant gets an in-app notification and an
              email (unless they've opted out of draw-finalized emails). A
              contestant entered more than once is listed — and notified —
              once per entry. The message uses the saved template; open
              <span className="font-semibold"> Edit template </span>
              first to review or change it.
              {notificationTemplate?.last_sent_at && (
                <span className="block mt-2 text-xs">
                  Last sent:{' '}
                  {new Date(notificationTemplate.last_sent_at).toLocaleString()}
                  {typeof notificationTemplate.last_sent_count === 'number' &&
                    ` (${notificationTemplate.last_sent_count} recipients)`}
                  . Sending again is allowed — it will deliver a fresh batch.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {recipientsLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading recipients…
            </div>
          ) : recipients.length === 0 ? (
            <div className="py-6 text-sm text-muted-foreground">
              No recipients found for this draw. Everyone entered may lack a
              linked account, or the draw isn't finalized.
            </div>
          ) : (
            <div className="flex flex-col min-h-0">
              {/* Master toggle */}
              <div className="flex items-center justify-between gap-3 border-b pb-2 mb-1">
                <label
                  htmlFor="notif-recipients-all"
                  className="text-sm font-semibold cursor-pointer"
                >
                  All recipients
                </label>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    {selectedRecipientIds.length} of {recipients.length} selected
                  </span>
                  <Switch
                    id="notif-recipients-all"
                    checked={allRecipientsSelected}
                    onCheckedChange={toggleAllRecipients}
                  />
                </div>
              </div>

              {/* Per-recipient toggles */}
              <div className="overflow-y-auto -mr-1 pr-1" style={{ maxHeight: '40vh' }}>
                {recipients.map((r) => {
                  const id = r.event_registration_id;
                  const checked = !deselectedRecipients.has(id);
                  const drawn =
                    r.performance_name && r.performance_name !== 'Not drawn'
                      ? `${r.performance_name}${r.draw_position && r.draw_position !== '—' ? ` · #${r.draw_position}` : ''}`
                      : 'Not drawn';
                  return (
                    <div
                      key={id}
                      className="flex items-center justify-between gap-3 py-2 border-b last:border-b-0"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {r.contestant_full_name}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {drawn}
                          {r.email ? (
                            <> · {r.email}</>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-600 ml-1">
                              <MailX className="h-3 w-3" /> no email — in-app only
                            </span>
                          )}
                        </div>
                      </div>
                      <Switch
                        checked={checked}
                        onCheckedChange={() => toggleRecipient(id)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSendOpen(false)}
              disabled={sendNotifications.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSendNotificationsConfirm}
              disabled={
                sendNotifications.isPending ||
                recipientsLoading ||
                selectedRecipientIds.length === 0
              }
            >
              {sendNotifications.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1.5" />
              )}
              Send to {selectedRecipientIds.length}
              {selectedRecipientIds.length === 1 ? ' recipient' : ' recipients'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel pending-invitation confirmation. Naming both contestants so
          the admin can verify which team they're scrapping. */}
      <Dialog
        open={!!cancelConfirmFor}
        onOpenChange={(open) => {
          if (!open) setCancelConfirmFor(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel pending invitation?</DialogTitle>
            <DialogDescription>
              {cancelConfirmFor && (
                <>
                  This will cancel {cancelConfirmFor.primary_name}'s pending
                  partner invitation to {cancelConfirmFor.partner_name}.{' '}
                  {cancelConfirmFor.partner_name}'s entry will be cancelled, and{' '}
                  {cancelConfirmFor.primary_name} will be demoted to{' '}
                  <em>needs partner</em> (still entered, but unable to draw
                  until they invite someone new). Both contestants are
                  notified by email + in-app notification.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelConfirmFor(null)}>
              Keep invitation
            </Button>
            <Button
              variant="destructive"
              disabled={cancelPending.isPending}
              onClick={async () => {
                if (!cancelConfirmFor) return;
                try {
                  await cancelPending.mutateAsync({
                    registration_id: cancelConfirmFor.primary_registration_id,
                  });
                  showSuccess(
                    'Invitation cancelled',
                    `${cancelConfirmFor.partner_name}'s entry was cancelled and ${cancelConfirmFor.primary_name} can now invite a new partner.`,
                  );
                  setCancelConfirmFor(null);
                } catch (err: any) {
                  showError(
                    'Could not cancel invitation',
                    err?.response?.data?.error ?? err?.message ?? 'Unknown error',
                  );
                }
              }}
            >
              {cancelPending.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : null}
              Cancel invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accept on behalf confirmation. Different dialog so the copy can
          state explicitly that the admin is overriding the contestant
          consent gate. */}
      <Dialog
        open={!!acceptConfirmFor}
        onOpenChange={(open) => {
          if (!open) setAcceptConfirmFor(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept invitation on behalf of contestant?</DialogTitle>
            <DialogDescription>
              {acceptConfirmFor && (
                <>
                  Mark {acceptConfirmFor.partner_name}'s invitation to team
                  with {acceptConfirmFor.primary_name} as <em>accepted</em>,
                  bypassing the contestant accept step. Only do this when
                  the contestant has agreed verbally / in writing — they will
                  receive a "you're entered" confirmation email immediately.
                  After this action the team is approved and eligible for the
                  draw.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptConfirmFor(null)}>
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={acceptOnBehalf.isPending}
              onClick={async () => {
                if (!acceptConfirmFor) return;
                try {
                  await acceptOnBehalf.mutateAsync({
                    registration_id: acceptConfirmFor.primary_registration_id,
                  });
                  showSuccess(
                    'Invitation accepted',
                    `${acceptConfirmFor.primary_name} + ${acceptConfirmFor.partner_name} is now an approved team.`,
                  );
                  setAcceptConfirmFor(null);
                } catch (err: any) {
                  showError(
                    'Could not accept invitation',
                    err?.response?.data?.error ?? err?.message ?? 'Unknown error',
                  );
                }
              }}
            >
              {acceptOnBehalf.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : null}
              Accept on behalf
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign confirmation. Spells out the full chain of consequences:
          who gets dropped, who gets paired, and the fee preview for the
          new partner (reused fee for the reuse path; charge / free toggle
          for the create-new-entry path). */}
      <Dialog
        open={!!reassignConfirm}
        onOpenChange={(open) => {
          if (!open) setReassignConfirm(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirm partner reassignment</DialogTitle>
            <DialogDescription>
              {reassignConfirm && (
                <span className="space-y-2 block">
                  <span className="block">
                    <strong>
                      {reassignConfirm.keepSide === 'primary'
                        ? reassignConfirm.team.primary_name
                        : reassignConfirm.team.partner_name}
                    </strong>{' '}
                    ({reassignConfirm.keepSide === 'primary'
                      ? reassignConfirm.team.primary_position
                      : reassignConfirm.team.partner_position}
                    ) will be paired with{' '}
                    <strong>{reassignConfirm.candidate.name}</strong>{' '}
                    {reassignConfirm.candidate.mode === 'reuse'
                      ? `(${reassignConfirm.candidate.position})`
                      : `(${(reassignConfirm.candidate as any).position})`}
                    .
                  </span>
                  <span className="block">
                    <strong>
                      {reassignConfirm.keepSide === 'primary'
                        ? reassignConfirm.team.partner_name
                        : reassignConfirm.team.primary_name}
                    </strong>{' '}
                    will{' '}
                    {reassignConfirm.keepSide === 'primary'
                      ? "be removed (their entry cancelled — they hadn't accepted)."
                      : 'become an entry without a partner (still in the event, but unable to draw until they invite someone new).'}
                  </span>
                  {reassignConfirm.candidate.mode === 'reuse' &&
                    reassignConfirm.candidate.approval === 'pending' &&
                    reassignConfirm.candidate.displaced_name && (
                      <span className="block">
                        <strong>{reassignConfirm.candidate.displaced_name}</strong>{' '}
                        will become an entry without a partner because their
                        pending team gets broken.
                      </span>
                    )}
                  {reassignConfirm.candidate.mode === 'reuse' && (
                    <span className="block text-xs">
                      {reassignConfirm.candidate.payment_owed ? (
                        <>
                          {reassignConfirm.candidate.name} still owes{' '}
                          {formatMoney(reassignConfirm.candidate.payment_amount ?? 0)}{' '}
                          on their existing entry — that balance is preserved.
                        </>
                      ) : (
                        <>
                          {reassignConfirm.candidate.name}'s existing entry
                          is fully paid — no fee changes.
                        </>
                      )}
                    </span>
                  )}
                  {reassignConfirm.candidate.mode === 'new_entry' && (
                    <span className="block space-y-1.5">
                      <span className="block text-xs">
                        {reassignConfirm.candidate.name} is already in an
                        approved team. That team stays intact — this creates a{' '}
                        <strong>second entry</strong> for them paired with{' '}
                        {reassignConfirm.keepSide === 'primary'
                          ? reassignConfirm.team.primary_name
                          : reassignConfirm.team.partner_name}
                        .
                      </span>
                      <span className="block text-xs font-medium pt-1">
                        Charge for the new entry:
                      </span>
                      <span className="flex flex-wrap gap-2 pt-0.5">
                        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                          <input
                            type="radio"
                            checked={reassignConfirm.fee_mode === 'normal'}
                            onChange={() =>
                              setReassignConfirm((s) =>
                                s ? { ...s, fee_mode: 'normal' } : s,
                              )
                            }
                            className="h-3.5 w-3.5"
                          />
                          <span>
                            Charge {reassignConfirm.candidate.name} the standard
                            entry fee ({formatMoney(reassignConfirm.candidate.new_entry_fee)})
                          </span>
                        </label>
                        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                          <input
                            type="radio"
                            checked={reassignConfirm.fee_mode === 'free'}
                            onChange={() =>
                              setReassignConfirm((s) =>
                                s ? { ...s, fee_mode: 'free' } : s,
                              )
                            }
                            className="h-3.5 w-3.5"
                          />
                          <span>Free ($0.00 — admin paid / filling performance)</span>
                        </label>
                      </span>
                    </span>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={reassignPartner.isPending}
              onClick={async () => {
                if (!reassignConfirm) return;
                const keepRegId =
                  reassignConfirm.keepSide === 'primary'
                    ? reassignConfirm.team.primary_registration_id
                    : reassignConfirm.team.partner_registration_id;
                const data: AdminReassignPartnerData = {
                  keep_registration_id: keepRegId,
                };
                if (reassignConfirm.candidate.mode === 'reuse') {
                  data.new_partner_registration_id =
                    reassignConfirm.candidate.registration_id;
                } else {
                  data.new_partner_user_profile_id =
                    reassignConfirm.candidate.user_profile_id;
                  data.new_partner_position = reassignConfirm.candidate.position;
                  data.fee_mode = reassignConfirm.fee_mode ?? 'normal';
                }
                try {
                  await reassignPartner.mutateAsync(data);
                  showSuccess(
                    'Partner reassigned',
                    `${
                      reassignConfirm.keepSide === 'primary'
                        ? reassignConfirm.team.primary_name
                        : reassignConfirm.team.partner_name
                    } is now teamed with ${reassignConfirm.candidate.name}.`,
                  );
                  setReassignConfirm(null);
                  setReassignOpenFor(null);
                } catch (err: any) {
                  showError(
                    'Could not reassign',
                    err?.response?.data?.error ?? err?.message ?? 'Unknown error',
                  );
                }
              }}
            >
              {reassignPartner.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : null}
              Confirm reassignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send-message composer for partnerless rows. Opens with a sensible
          default subject + body the admin can edit before sending. The
          email is sent FROM support@highcallrodeo.com so the recipient
          can reply (e.g. naming a partner they'd like to be assigned). */}
      <Dialog
        open={!!messageModalFor}
        onOpenChange={(open) => {
          if (!open) {
            setMessageModalFor(null);
            setMessageSubject('');
            setMessageBody('');
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              Send message to {messageModalFor?.name ?? 'contestant'}
            </DialogTitle>
            <DialogDescription>
              An email will be sent from <strong>support@highcallrodeo.com</strong> so
              the contestant can reply to a monitored inbox. The recipient
              will also see this in their in-app notifications. Sent in the
              standard HighCall email template.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-foreground/80">Subject</label>
              <input
                type="text"
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
                maxLength={200}
                className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground/80">Message</label>
              <textarea
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                rows={8}
                maxLength={5000}
                className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <div className="text-[10px] text-muted-foreground mt-1 text-right">
                {messageBody.length} / 5000
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setMessageModalFor(null);
                setMessageSubject('');
                setMessageBody('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={
                sendCustomMessage.isPending ||
                !messageSubject.trim() ||
                !messageBody.trim()
              }
              onClick={async () => {
                if (!messageModalFor) return;
                try {
                  await sendCustomMessage.mutateAsync({
                    registration_id: messageModalFor.id,
                    subject: messageSubject.trim(),
                    body: messageBody.trim(),
                  });
                  showSuccess(
                    'Message sent',
                    `${messageModalFor.name} was emailed (from support@highcallrodeo.com).`,
                  );
                  setMessageModalFor(null);
                  setMessageSubject('');
                  setMessageBody('');
                } catch (err: any) {
                  showError(
                    'Could not send message',
                    err?.response?.data?.error ?? err?.message ?? 'Unknown error',
                  );
                }
              }}
            >
              {sendCustomMessage.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : null}
              Send message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Non-destructive reassurance — only relevant while the rodeo is
          still in sandbox mode. Once Initialize Draw has flipped the
          rodeo to live, every run mutates the persisted ferris-wheel
          state, so showing "this is a sandbox draw" would be misleading.
          The InitializeDrawPanel at the top of the screen also hides in
          live mode, so the two stay in sync. */}
      {isSandboxMode && (
        <div className="rounded-md border border-blue-300/50 bg-blue-50 dark:bg-blue-500/5 dark:border-blue-900/60 px-4 py-2.5 text-sm flex items-start gap-2">
          <Shuffle className="h-4 w-4 mt-0.5 shrink-0 text-blue-600 dark:text-blue-400" />
          <span className="text-blue-900 dark:text-blue-200">
            This is a sandbox draw. Generating or re-running does <strong>not</strong>{' '}
            change live entries, payments, or signups. Re-runs are deterministic
            for members (weight-driven); only non-members and any newly added
            members shift between runs.
          </span>
        </div>
      )}

      {/* Pending-approval team-event entries — invitation sent, NOT yet
          accepted by the partner. The backend draw guard rejects these
          (TEAM_ROPING_PENDING_INVITATIONS) because pairing two contestants
          into a team neither has confirmed is exactly the bug that surfaced
          on Cambridge June 2026. Surfacing them here, with the contestants
          named and the invitation age visible, gives the admin enough
          context to choose between: Cancel the invite outright, Accept
          on the invitee's behalf (when consent is verbal), or Reassign
          one side to a different partner. */}
      {pendingTeams.length > 0 && (
        <div className="rounded-md border border-orange-400 bg-orange-50 dark:bg-orange-950/30 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-orange-900 dark:text-orange-100">
                {pendingTeams.length} team
                {pendingTeams.length === 1 ? '' : 's'} with an unaccepted
                partner invitation
              </h3>
              <p className="text-sm text-orange-800 dark:text-orange-200 mt-0.5">
                The invited partner has not yet accepted. These teams will
                NOT be included in the draw until they're approved. Resolve
                each below before publishing.
              </p>
              <ul className="mt-3 space-y-2 text-sm">
                {pendingTeams.map((team) => {
                  const isReassignOpen = reassignOpenFor === team.primary_registration_id;
                  const keep =
                    reassignKeepSide === 'primary'
                      ? {
                          regId: team.primary_registration_id,
                          name: team.primary_name,
                          position: team.primary_position,
                          userProfileId: team.primary_user_profile_id,
                        }
                      : {
                          regId: team.partner_registration_id,
                          name: team.partner_name,
                          position: team.partner_position,
                          userProfileId: team.partner_user_profile_id,
                        };
                  // Candidates for the keep side: opposite-position rows on
                  // this event. Three groups powering the 3-bucket dropdown:
                  // unpaired, in-pending, in-approved. Exclude the keep side
                  // itself and exclude same user_profile_id (no self-pair).
                  const candidates = allTeamRegs.filter(
                    (other) =>
                      other.id !== keep.regId &&
                      other.position !== keep.position &&
                      (!other.user_profile_id ||
                        !keep.userProfileId ||
                        other.user_profile_id !== keep.userProfileId) &&
                      // Also exclude the OTHER side of THIS pending team
                      // (we're dropping them, so picking them as new
                      // partner would just re-create the same pending
                      // team after the round-trip).
                      other.id !==
                        (reassignKeepSide === 'primary'
                          ? team.partner_registration_id
                          : team.primary_registration_id),
                  );
                  const unpairedCands = candidates.filter((c) => c.approval === 'unpaired');
                  const pendingCands = candidates.filter((c) => c.approval === 'pending');
                  const approvedCands = candidates.filter((c) => c.approval === 'approved');
                  const isCancelling =
                    cancelPending.isPending &&
                    cancelPending.variables?.registration_id === team.primary_registration_id;
                  const isAccepting =
                    acceptOnBehalf.isPending &&
                    acceptOnBehalf.variables?.registration_id === team.primary_registration_id;
                  const isReassigning =
                    reassignPartner.isPending &&
                    reassignPartner.variables?.keep_registration_id === keep.regId;
                  return (
                    <li
                      key={team.primary_registration_id}
                      className="rounded border border-orange-300/70 bg-white/50 dark:bg-orange-950/20 px-3 py-2"
                    >
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-medium text-orange-900 dark:text-orange-100">
                          {team.primary_name}
                        </span>
                        {team.primary_position && (
                          <Badge
                            variant="outline"
                            className="text-[10px] border-orange-400 text-orange-800 dark:text-orange-200"
                          >
                            {team.primary_position}
                          </Badge>
                        )}
                        <span className="text-xs text-orange-700 dark:text-orange-300">
                          invited
                        </span>
                        <span className="font-medium text-orange-900 dark:text-orange-100">
                          {team.partner_name}
                        </span>
                        {team.partner_position && (
                          <Badge
                            variant="outline"
                            className="text-[10px] border-orange-400 text-orange-800 dark:text-orange-200"
                          >
                            {team.partner_position}
                          </Badge>
                        )}
                        <span className="text-xs text-orange-700 dark:text-orange-300">
                          · invited {formatInvitedAgo(team.partner_invited_at)}
                        </span>
                        <span className="text-xs text-orange-700 dark:text-orange-300 ml-auto">
                          {team.primary_pending_payment
                            ? `${team.primary_name} owes ${formatMoney(team.primary_payment_amount ?? 0)}`
                            : `${team.primary_name}: paid`}
                          {' · '}
                          {team.partner_pending_payment
                            ? `${team.partner_name} owes ${formatMoney(team.partner_payment_amount ?? 0)}`
                            : `${team.partner_name}: paid`}
                        </span>
                      </div>
                      {!isLocked && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {(() => {
                            const isRemindBusy =
                              sendPartnerReminder.isPending &&
                              sendPartnerReminder.variables?.registration_id ===
                                team.primary_registration_id;
                            const lastSent = team.partner_reminder_last_sent_at;
                            return (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs border-orange-400 text-orange-900 hover:bg-orange-100 dark:text-orange-100 dark:hover:bg-orange-900/40"
                                disabled={isRemindBusy}
                                onClick={async () => {
                                  try {
                                    await sendPartnerReminder.mutateAsync({
                                      registration_id: team.primary_registration_id,
                                    });
                                    showSuccess(
                                      'Reminder sent',
                                      `${team.partner_name} was nudged to respond to the invitation.`,
                                    );
                                  } catch (err: any) {
                                    showError(
                                      'Could not send reminder',
                                      err?.response?.data?.error ??
                                        err?.message ??
                                        'Unknown error',
                                    );
                                  }
                                }}
                              >
                                {isRemindBusy
                                  ? 'Sending…'
                                  : lastSent
                                    ? `Resend reminder (last ${formatInvitedAgo(lastSent)})`
                                    : 'Send reminder'}
                              </Button>
                            );
                          })()}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs border-orange-400 text-orange-900 hover:bg-orange-100 dark:text-orange-100 dark:hover:bg-orange-900/40"
                            disabled={isCancelling}
                            onClick={() => setCancelConfirmFor(team)}
                          >
                            {isCancelling ? 'Cancelling…' : 'Cancel invitation'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs border-emerald-400 text-emerald-900 hover:bg-emerald-100 dark:text-emerald-100 dark:hover:bg-emerald-900/40"
                            disabled={isAccepting}
                            onClick={() => setAcceptConfirmFor(team)}
                          >
                            {isAccepting ? 'Accepting…' : 'Accept on behalf'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs border-orange-400 text-orange-900 hover:bg-orange-100 dark:text-orange-100 dark:hover:bg-orange-900/40"
                            onClick={() => {
                              if (isReassignOpen) {
                                setReassignOpenFor(null);
                                return;
                              }
                              setReassignOpenFor(team.primary_registration_id);
                              setReassignKeepSide('primary');
                            }}
                          >
                            {isReassignOpen ? 'Close reassign' : 'Reassign…'}
                          </Button>
                        </div>
                      )}
                      {isReassignOpen && !isLocked && (
                        <div className="mt-3 rounded border border-orange-300 bg-orange-50 dark:bg-orange-950/40 p-3 space-y-3">
                          <div className="text-xs text-orange-900 dark:text-orange-100 font-semibold">
                            Keep which contestant in this team?
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                              <input
                                type="radio"
                                checked={reassignKeepSide === 'primary'}
                                onChange={() => setReassignKeepSide('primary')}
                                className="h-3.5 w-3.5"
                              />
                              <span>
                                Keep {team.primary_name} ({team.primary_position}) —{' '}
                                {team.partner_name} drops off
                              </span>
                            </label>
                            <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                              <input
                                type="radio"
                                checked={reassignKeepSide === 'partner'}
                                onChange={() => setReassignKeepSide('partner')}
                                className="h-3.5 w-3.5"
                              />
                              <span>
                                Keep {team.partner_name} ({team.partner_position}) —{' '}
                                {team.primary_name} drops off
                              </span>
                            </label>
                          </div>
                          <div>
                            <div className="text-xs text-orange-900 dark:text-orange-100 font-semibold mb-1.5">
                              Pick {keep.name}'s new partner
                            </div>
                            <Select
                              key={`${team.primary_registration_id}-${reassignKeepSide}`}
                              onValueChange={(value) => {
                                if (!value) return;
                                // Encode each option as either
                                //   reuse:<reg_id>
                                //   new:<user_profile_id>:<position>
                                // so the same Select can drive both modes.
                                if (value.startsWith('reuse:')) {
                                  const regId = value.slice('reuse:'.length);
                                  const cand = candidates.find((c) => c.id === regId);
                                  if (!cand) return;
                                  let displacedName: string | null = null;
                                  if (
                                    cand.approval === 'pending' &&
                                    cand.partner_registration_id
                                  ) {
                                    const other = allTeamRegs.find(
                                      (x) => x.id === cand.partner_registration_id,
                                    );
                                    displacedName = other?.name ?? null;
                                  }
                                  setReassignConfirm({
                                    team,
                                    keepSide: reassignKeepSide,
                                    candidate: {
                                      mode: 'reuse',
                                      registration_id: cand.id,
                                      name: cand.name,
                                      position: cand.position,
                                      approval:
                                        cand.approval === 'unpaired' ? 'unpaired' : 'pending',
                                      payment_amount: cand.payment_amount,
                                      payment_owed: cand.payment_owed,
                                      displaced_name: displacedName,
                                    },
                                  });
                                } else if (value.startsWith('new:')) {
                                  const [, profileId, position] = value.split(':');
                                  const cand = candidates.find(
                                    (c) =>
                                      c.user_profile_id === profileId &&
                                      c.position === position,
                                  );
                                  if (!cand) return;
                                  setReassignConfirm({
                                    team,
                                    keepSide: reassignKeepSide,
                                    candidate: {
                                      mode: 'new_entry',
                                      user_profile_id: profileId,
                                      name: cand.name,
                                      position: position as 'Header' | 'Heeler',
                                      existing_entry_payment: cand.payment_amount,
                                      new_entry_fee: eventEntryFee,
                                    },
                                    fee_mode: 'normal',
                                  });
                                }
                              }}
                            >
                              <SelectTrigger className="h-7 text-xs w-full bg-white dark:bg-orange-950/40 border-orange-400">
                                <SelectValue placeholder="Choose new partner…" />
                              </SelectTrigger>
                              <SelectContent>
                                {unpairedCands.length > 0 && (
                                  <SelectGroup>
                                    <SelectLabel className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                                      Unassigned (reuses existing entry)
                                    </SelectLabel>
                                    {unpairedCands.map((c) => (
                                      <SelectItem
                                        key={`reuse:${c.id}`}
                                        value={`reuse:${c.id}`}
                                        className="text-xs"
                                      >
                                        {c.name}
                                        {c.position ? ` (${c.position})` : ''}
                                        {' · '}
                                        {c.payment_owed
                                          ? `still owes ${formatMoney(c.payment_amount ?? 0)}`
                                          : 'paid / no fee'}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                )}
                                {unpairedCands.length > 0 && pendingCands.length > 0 && (
                                  <SelectSeparator />
                                )}
                                {pendingCands.length > 0 && (
                                  <SelectGroup>
                                    <SelectLabel className="text-[10px] uppercase tracking-wide text-orange-700 dark:text-orange-400">
                                      In pending team (will be broken)
                                    </SelectLabel>
                                    {pendingCands.map((c) => (
                                      <SelectItem
                                        key={`reuse:${c.id}`}
                                        value={`reuse:${c.id}`}
                                        className="text-xs"
                                      >
                                        {c.name}
                                        {c.position ? ` (${c.position})` : ''}
                                        {c.partner_name ? (
                                          <span className="text-muted-foreground">
                                            {' '}
                                            · pending w/ {c.partner_name}
                                          </span>
                                        ) : null}
                                        {' · '}
                                        {c.payment_owed
                                          ? `still owes ${formatMoney(c.payment_amount ?? 0)}`
                                          : 'paid / no fee'}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                )}
                                {(unpairedCands.length > 0 || pendingCands.length > 0) &&
                                  approvedCands.length > 0 && <SelectSeparator />}
                                {approvedCands.length > 0 && (
                                  <SelectGroup>
                                    <SelectLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                      Already approved (creates new entry)
                                    </SelectLabel>
                                    {approvedCands.map((c) => (
                                      <SelectItem
                                        key={`new:${c.user_profile_id ?? c.id}:${c.position ?? ''}`}
                                        value={`new:${c.user_profile_id ?? ''}:${c.position ?? ''}`}
                                        className="text-xs"
                                      >
                                        {c.name}
                                        {c.position ? ` (${c.position})` : ''}
                                        {c.partner_name ? (
                                          <span className="text-muted-foreground">
                                            {' '}
                                            · w/ {c.partner_name}
                                          </span>
                                        ) : null}
                                        {' · '}new entry @ {formatMoney(eventEntryFee)}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                )}
                                {unpairedCands.length === 0 &&
                                  pendingCands.length === 0 &&
                                  approvedCands.length === 0 && (
                                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                      No eligible{' '}
                                      {keep.position === 'Header' ? 'heelers' : 'headers'}{' '}
                                      on this event.
                                    </div>
                                  )}
                              </SelectContent>
                            </Select>
                            {isReassigning && (
                              <div className="text-xs text-muted-foreground mt-1">
                                Applying reassign…
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Partnerless team-event entries — these contestants have entries
          but the draw refuses to include them (draw_eligible=false) until
          a partner is on the row. Without this callout they're invisible
          on this screen (they don't make it into the draft), so the admin
          has no way to pair them up or draw them out. */}
      {partnerlessRegs.length > 0 && (
        <div className="rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                {partnerlessRegs.length} entr
                {partnerlessRegs.length === 1 ? 'y' : 'ies'} without an approved
                partner
              </h3>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-0.5">
                These contestants registered but their entry will NOT be
                included in the draw until they have a partner. Pair two of
                them together below, or cancel/draw them out before
                publishing.
              </p>
              <ul className="mt-3 space-y-1 text-sm">
                {partnerlessRegs.slice(0, 20).map((r) => {
                  // Candidates the admin can pair this row with: every
                  // opposite-position row on the event (Header pairs with
                  // Heeler), split into two buckets:
                  //   • Unpaired  — currently has no partner; safe pick.
                  //   • Already paired — currently linked to someone else;
                  //     surfaced so the admin can SEE who's already taken,
                  //     and selectable too (backend will reject with a
                  //     specific 409 code if they pick one, which we surface
                  //     verbatim — see PARTNER_ALREADY_SET / PARTNER_LINK_EXISTS).
                  // Excluding self covers the case where the same
                  // user_profile_id somehow holds both halves.
                  const allCandidates = allTeamRegs.filter(
                    (other) =>
                      other.id !== r.id &&
                      !!other.position &&
                      !!r.position &&
                      other.position !== r.position &&
                      (!other.user_profile_id ||
                        !r.user_profile_id ||
                        other.user_profile_id !== r.user_profile_id),
                  );
                  const unpairedCandidates = allCandidates.filter((c) => !c.isPaired);
                  const pairedCandidates = allCandidates.filter((c) => c.isPaired);
                  const hasAnyCandidate = allCandidates.length > 0;
                  const isOpen = pairingOpenFor === r.id;
                  const isBusy =
                    pairPartnerless.isPending &&
                    (pairPartnerless.variables?.primary_registration_id === r.id ||
                      pairPartnerless.variables?.partner_registration_id === r.id);
                  return (
                    <li
                      key={r.id}
                      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-amber-900 dark:text-amber-100"
                    >
                      <span className="font-medium">{r.name}</span>
                      {r.position && (
                        <Badge
                          variant="outline"
                          className="text-[10px] border-amber-400 text-amber-800 dark:text-amber-200"
                        >
                          {r.position}
                        </Badge>
                      )}
                      {r.status === 'needs_partner' && (
                        <span className="text-xs text-amber-700 dark:text-amber-300">
                          — previous partner declined
                        </span>
                      )}
                      {(r.status === 'none' ||
                        r.status === 'cancelled' ||
                        r.status === 'rejected' ||
                        r.status == null) && (
                        <span className="text-xs text-amber-700 dark:text-amber-300">
                          — never had a partner
                        </span>
                      )}
                      {/* Send-message button. Shown next to Pair-with so the
                          admin can reach out to the contestant ("Hey, did you
                          have a partner in mind?") without first pairing them.
                          Label shifts to "Re-send / last Xh ago" once a
                          message has been sent on this row so the admin can
                          see whether they already pinged this contestant. */}
                      {!isLocked && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs border-amber-400 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/40 ml-auto"
                          onClick={() => {
                            setMessageModalFor({
                              ...r,
                              event_name: (drawEvent as any)?.event_name ?? null,
                            });
                            setMessageSubject(
                              r.position
                                ? `Your team-roping entry for ${
                                    (drawEvent as any)?.event_name ?? 'an event'
                                  }`
                                : `Your entry for ${
                                    (drawEvent as any)?.event_name ?? 'an event'
                                  }`,
                            );
                            setMessageBody(
                              `Hi ${r.name},\n\nWe noticed your entry for the upcoming draw doesn't have a confirmed partner yet. Do you have someone in mind you'd like to partner with? Reply to this email and we'll help get the team set up.\n\nThanks,\nHighCall Rodeo`,
                            );
                          }}
                        >
                          {r.admin_message_last_sent_at
                            ? `Re-send message (last ${formatInvitedAgo(r.admin_message_last_sent_at)})`
                            : 'Send message'}
                        </Button>
                      )}
                      {/* Lock the Pair-with control once the draw is published
                          or finalized — same rule the rest of the draw-mutating
                          controls follow. The bar above the list still shows
                          the unpaired roster so the admin sees who got left
                          behind; they just can't reassign anyone post-publish. */}
                      {hasAnyCandidate && !isOpen && !isLocked && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs border-amber-400 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/40"
                          disabled={isBusy}
                          onClick={() => setPairingOpenFor(r.id)}
                        >
                          {isBusy ? 'Pairing…' : 'Pair with…'}
                        </Button>
                      )}
                      {isOpen && !isLocked && (
                        <div className="flex flex-wrap items-center gap-1 ml-auto">
                          <Select
                            onValueChange={async (partnerId) => {
                              if (!partnerId) return;
                              try {
                                await pairPartnerless.mutateAsync({
                                  primary_registration_id: r.id,
                                  partner_registration_id: partnerId,
                                });
                                showSuccess(
                                  'Team paired',
                                  `${r.name} is now in the draw.`,
                                );
                                setPairingOpenFor(null);
                              } catch (err: any) {
                                // Surface the backend's typed message verbatim
                                // when present (e.g. PARTNER_ALREADY_SET) so the
                                // admin understands why an Already-paired pick
                                // was rejected. The current pair endpoint won't
                                // displace an existing partner — that's a
                                // separate future enhancement; until then this
                                // toast is the safety net.
                                showError(
                                  'Could not pair',
                                  err?.response?.data?.error ??
                                    err?.message ??
                                    'Unknown error',
                                );
                              }
                            }}
                          >
                            <SelectTrigger className="h-7 text-xs w-[240px] border-amber-400 bg-white dark:bg-amber-950/40">
                              <SelectValue placeholder="Choose partner…" />
                            </SelectTrigger>
                            <SelectContent>
                              {unpairedCandidates.length > 0 && (
                                <SelectGroup>
                                  <SelectLabel className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                                    Unpaired
                                  </SelectLabel>
                                  {unpairedCandidates.map((c) => (
                                    <SelectItem
                                      key={c.id}
                                      value={c.id}
                                      className="text-xs"
                                    >
                                      {c.name}
                                      {c.position ? ` (${c.position})` : ''}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              )}
                              {unpairedCandidates.length > 0 &&
                                pairedCandidates.length > 0 && <SelectSeparator />}
                              {pairedCandidates.length > 0 && (
                                <SelectGroup>
                                  <SelectLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                    Already paired
                                  </SelectLabel>
                                  {pairedCandidates.map((c) => (
                                    <SelectItem
                                      key={c.id}
                                      value={c.id}
                                      className="text-xs"
                                    >
                                      {c.name}
                                      {c.position ? ` (${c.position})` : ''}
                                      {c.partner_name ? (
                                        <span className="text-muted-foreground">
                                          {' '}
                                          · w/ {c.partner_name}
                                        </span>
                                      ) : null}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              )}
                              {unpairedCandidates.length === 0 &&
                                pairedCandidates.length === 0 && (
                                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                    No eligible {r.position === 'Header' ? 'heelers' : 'headers'} on this event.
                                  </div>
                                )}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs text-amber-800 dark:text-amber-200"
                            onClick={() => setPairingOpenFor(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
                {partnerlessRegs.length > 20 && (
                  <li className="text-xs text-amber-700 dark:text-amber-300">
                    …and {partnerlessRegs.length - 20} more
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Conflict + warning banner is lifted ABOVE the loading/empty/full
          conditional so it shows even when the draft has zero entries
          (e.g. a rough-stock event where every performance reports
          zero placed entries — the conflict aggregator emits a warning
          per performance, but `hasDraft` is false so the per-event
          drilldown otherwise hides everything except the empty state).
          Lifting it here is what lets admins land on Bull Riding from a
          sibling-event warning and see WHY they're here. */}
      {(conflictCount > 0 || (draft?.warnings?.length ?? 0) > 0) && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-500/5 dark:border-amber-900/60 p-3 text-sm">
          <div className="flex items-center gap-2 font-medium text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4" />
            {conflictCount} conflict{conflictCount === 1 ? '' : 's'}
            {(draft?.warnings?.length ?? 0) > 0 &&
              ` · ${draft!.warnings.length} warning(s)`}
          </div>
          {/* Conflict messages — list EVERY conflict, not a truncated
              preview. Admins resolve conflicts from this per-event view,
              so a "…and N more" cap hid the very items they came here to
              fix. The conflicts endpoint already returns the full,
              event-scoped set (no server-side cap), so render all of them. */}
          {(conflicts?.conflicts ?? []).map((c, i) => (
            <div
              key={`conflict-${i}`}
              className="text-xs text-amber-800 dark:text-amber-300/80 mt-1 flex items-start gap-1.5"
            >
              <span
                className={`shrink-0 mt-0.5 text-[10px] uppercase tracking-wide font-semibold ${
                  c.severity === 'error'
                    ? 'text-red-700 dark:text-red-400'
                    : 'text-amber-700 dark:text-amber-400'
                }`}
              >
                {c.severity}
              </span>
              <span>{c.message}</span>
            </div>
          ))}
          {(draft?.warnings ?? []).map((w, i) => (
            <div key={`warn-${i}`} className="text-xs text-amber-800 dark:text-amber-300/80 mt-1">
              {humanizeWarningPerformanceIds(w, perfs)}
            </div>
          ))}
        </div>
      )}

      {capLoading || draftLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : !hasDraft ? (
        <div className="rounded-lg border bg-muted/40 p-10 text-center">
          <Shuffle className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          {placementStatus === 'draft' ? (
            // Stale-empty-draft case: an execution row exists but no
            // entries are placed against it. The conflict aggregator
            // fires the "zero entries for scored event" warning off this
            // row, so calling this "no draw yet" would mislead the admin
            // into thinking nothing needs cleanup. Direct them to the
            // Reset button (now visible thanks to the placementStatus
            // gate above) or to re-running Generate.
            <p className="text-muted-foreground">
              A draft draw exists for this event but no contestants are
              placed. Click <strong>Generate draft draw</strong> to re-run
              placement, or <strong>Reset</strong> above to clear the empty
              draft (this also clears any "zero entries for scored event"
              warnings produced by it).
            </p>
          ) : (
            <p className="text-muted-foreground">
              No draw yet. Click <strong>Generate draft draw</strong> to build a
              non-destructive draft from current entries and member weights.
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <StatCard label="Total entries" value={distinctEntryCount} />
            <StatCard label="Placed" value={placedCount} tone="emerald" />
            {namedPerfs.map((p) => (
              <StatCard
                key={p.performance_id}
                label={perfTitle(p)}
                value={(byPerf.get(p.performance_id) ?? []).length}
                sub={`cap ${p.slot_limit || '∞'}`}
              />
            ))}
            <StatCard label="Slack" value={slackCount} tone="amber" />
            <StatCard
              label="Draw outs"
              value={drawOuts.length}
              tone={drawOuts.length > 0 ? 'red' : undefined}
            />
            <StatCard
              label="Not drawn"
              value={undrawn.length}
              tone={undrawn.length > 0 ? 'red' : undefined}
            />
          </div>

          {/* Past-due fee warning — contestants in this draw who still owe
              entry fees from a rodeo that has already FINISHED. Surfaced at
              the top so an admin/delegate can decide, per contestant,
              whether to keep them in the draw or move them out (their other
              upcoming entries are untouched). */}
          {feeOwingContestants.length > 0 && (
            <div
              role="alert"
              className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle
                  className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                    {feeOwingContestants.length} contestant
                    {feeOwingContestants.length === 1 ? '' : 's'} owe unpaid
                    fees from a finished rodeo
                  </p>
                  <p className="text-[12px] text-red-700/90 dark:text-red-400/90 mb-2">
                    Review before drawing — keep them in or move them out of
                    this draw. Their entries in other (upcoming) rodeos are not
                    affected.
                  </p>
                  <ul className="space-y-1.5">
                    {feeOwingContestants.map((c) => (
                      <li
                        key={`${c.userId ?? c.name}|${c.fees.total_owed.toFixed(
                          2,
                        )}`}
                        className="text-[12px] text-red-800 dark:text-red-300"
                      >
                        <div className="flex flex-wrap items-baseline gap-x-1.5">
                          <ContestantNameLink
                            name={c.name}
                            userId={c.userId}
                            className="font-medium hover:underline"
                          />
                          <span className="tabular-nums font-semibold">
                            {formatMoney(c.fees.total_owed)}
                          </span>
                        </div>
                        <ul className="ml-3 mt-0.5 space-y-0.5">
                          {c.fees.items.map((i, idx) => (
                            <li
                              key={`${i.event_id}-${idx}`}
                              className="text-[11px] text-red-700/90 dark:text-red-400/90"
                            >
                              {pastDueItemLabel(i)}:{' '}
                              <span className="tabular-nums font-medium">
                                {formatMoney(i.amount_owed)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Preference filter badges + colour legend. Hidden on a finals
              rodeo: preferences are disabled there, so every badge would
              read 0 and the legend would describe colours that never
              appear. */}
          {!isFinals && (
          <div className="flex items-center gap-2 flex-wrap">
            <FilterBadge label="All" count={counts.all} active={filter === 'all'} onClick={() => setFilter('all')} />
            <FilterBadge label="1st pref" count={counts.first} tone="green" active={filter === 'first'} onClick={() => setFilter('first')} />
            <FilterBadge label="2nd pref" count={counts.second} tone="orange" active={filter === 'second'} onClick={() => setFilter('second')} />
            <FilterBadge label="Neither" count={counts.none} tone="red" active={filter === 'none'} onClick={() => setFilter('none')} />
            <FilterBadge label="Draw-out" count={counts.draw_out} tone="drawout" active={filter === 'draw_out'} onClick={() => setFilter('draw_out')} />
            <span className="text-[11px] text-muted-foreground ml-auto">
              green = 1st preference · orange = 2nd · red = neither · striped = draw-out
            </span>
          </div>
          )}

          {/* Draw-out section — its own block, at the very top. These
              contestants are NOT in any performance: their draw-out
              (2nd pref) means honor 1st pref or remove from the rodeo. */}
          {drawOuts.length > 0 && (
            <DrawList
              title="⚠ Draw outs — not in a performance"
              tone="red"
              rows={drawOuts}
              perfs={perfs}
              filter={filter}
              onMove={handleMove}
              moving={updateEntry.isPending}
              disabled={isLocked}
            />
          )}

          {/* Other not-drawn / removed (non-draw-out) */}
          {undrawn.length > 0 && (
            <DrawList
              title="Not drawn / removed"
              tone="red"
              info={
                <>
                  <p className="font-semibold mb-1">
                    Entries here are NOT drawn outs.
                  </p>
                  <p className="mb-1">
                    A contestant lands in this section when they have no
                    performance assignment and did NOT pick &ldquo;Draw
                    Out&rdquo; as a preference. Common causes:
                  </p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    <li>
                      Registered <em>after</em> the draft was generated
                      (re-run the draw to slot them in).
                    </li>
                    <li>
                      Manually removed from a performance by an admin.
                    </li>
                    <li>
                      No preferences set — the algorithm had nothing to
                      honour.
                    </li>
                    <li>
                      Every preferred performance (incl. slack, if any) was
                      at capacity.
                    </li>
                    <li>
                      Team-roping entry that never paired with a partner.
                    </li>
                    <li>
                      Buddy group couldn&rsquo;t fit together in any single
                      performance.
                    </li>
                  </ul>
                  <p className="mt-1">
                    Each row shows its specific reason below the
                    preferences.
                  </p>
                </>
              }
              rows={undrawn}
              perfs={perfs}
              filter={filter}
              onMove={handleMove}
              moving={updateEntry.isPending}
              disabled={isLocked}
            />
          )}

          {/* Per named performance */}
          {namedPerfs.map((p) => (
            <DrawList
              key={p.performance_id}
              title={perfTitle(p)}
              subtitle={`${(byPerf.get(p.performance_id) ?? []).length} of ${
                p.slot_limit || '∞'
              }`}
              rows={byPerf.get(p.performance_id) ?? []}
              perfs={perfs}
              filter={filter}
              onMove={handleMove}
              moving={updateEntry.isPending}
              disabled={isLocked}
              orderable
              performanceId={p.performance_id}
              onReorder={handleReorder}
              reordering={reorderPerf.isPending}
              runOrder={
                isFinals
                  ? {
                      value: winningsOrder[p.performance_id]?.mode ?? 'draw',
                      onChange: (mode) => handleRunOrderChange(p, mode),
                      pending: reorderByWinnings.isPending,
                      amounts: winningsOrder[p.performance_id]?.amounts ?? null,
                      amountKind: winningsOrder[p.performance_id]
                        ? winningsOrder[p.performance_id].mode.startsWith('ytd')
                          ? 'YTD'
                          : 'current'
                        : null,
                    }
                  : undefined
              }
            />
          ))}

          {/* Slack */}
          {slackPerfs.map((p) => (
            <DrawList
              key={p.performance_id}
              title={perfTitle(p)}
              tone="amber"
              subtitle={`${(byPerf.get(p.performance_id) ?? []).length} in slack`}
              rows={byPerf.get(p.performance_id) ?? []}
              perfs={perfs}
              filter={filter}
              onMove={handleMove}
              moving={updateEntry.isPending}
              disabled={isLocked}
              orderable
              performanceId={p.performance_id}
              onReorder={handleReorder}
              reordering={reorderPerf.isPending}
            />
          ))}
        </>
      )}

      {/* Hidden-by-default printable judge sheet. Lives outside the normal
          DOM tree (portaled to document.body inside JudgeSheetPrintable)
          so the print stylesheet in src/index.css can hide every sibling
          and leave only the .judge-sheet-print-portal visible. Rendered
          conditionally so non-print views don't allocate the DOM at all. */}
      {canPrintJudgeSheet && (
        <JudgeSheetPrintable
          organizationName={(daySheet as any)?.organization_name ?? ''}
          rodeoName={(daySheet as any)?.rodeo_name ?? ''}
          roundLabel="1st Go Round"
          eventTypeName={
            (daySheet as any)?.event_type_name ??
            (daySheet as any)?.event_name ??
            ''
          }
          eventDate={judgeSheetEventDateLabel}
          columns={judgeSheetLayout.columns}
          minRows={judgeSheetLayout.minRows}
          performances={judgeSheetPerformances}
        />
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: number;
  sub?: string;
  tone?: 'emerald' | 'amber' | 'red';
}) {
  const toneCls =
    tone === 'emerald'
      ? 'text-emerald-600 dark:text-emerald-400'
      : tone === 'amber'
        ? 'text-amber-600 dark:text-amber-400'
        : tone === 'red'
          ? 'text-red-600 dark:text-red-400'
          : '';
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className={`text-2xl font-bold ${toneCls}`}>{value}</div>
      <div className="text-xs text-muted-foreground truncate">{label}</div>
      {sub && <div className="text-[10px] text-muted-foreground/60">{sub}</div>}
    </div>
  );
}

function FilterBadge({
  label,
  count,
  active,
  tone,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  tone?: 'green' | 'orange' | 'red' | 'drawout';
  onClick: () => void;
}) {
  const toneRing =
    tone === 'green'
      ? 'data-[on=true]:bg-emerald-600 data-[on=true]:text-white border-emerald-400 text-emerald-700 dark:text-emerald-400'
      : tone === 'orange'
        ? 'data-[on=true]:bg-orange-500 data-[on=true]:text-white border-orange-400 text-orange-700 dark:text-orange-400'
        : tone === 'red'
          ? 'data-[on=true]:bg-red-600 data-[on=true]:text-white border-red-400 text-red-700 dark:text-red-400'
          : tone === 'drawout'
            ? 'data-[on=true]:bg-red-700 data-[on=true]:text-white border-red-600 text-red-700 dark:text-red-400 font-semibold'
            : 'data-[on=true]:bg-foreground data-[on=true]:text-background border-border';
  return (
    <button
      type="button"
      data-on={active}
      onClick={onClick}
      className={`h-7 px-2.5 rounded-full border text-xs flex items-center gap-1.5 transition-colors ${toneRing}`}
    >
      {label}
      <span className="tabular-nums opacity-80">{count}</span>
    </button>
  );
}

// Maps the server-computed preference outcome to row colour. Draw-outs
// get the strongest treatment (bright red + left border + diagonal
// stripe) so they can't be missed.
function rowStyle(e: DraftEntryRow): string {
  switch (e.preference_outcome) {
    case 'draw_out':
      return 'border-l-4 border-l-red-600 bg-[repeating-linear-gradient(45deg,rgba(220,38,38,0.10)_0_8px,transparent_8px_16px)] dark:bg-[repeating-linear-gradient(45deg,rgba(248,113,113,0.12)_0_8px,transparent_8px_16px)]';
    case 'first':
      return 'border-l-4 border-l-emerald-500 bg-emerald-50/50 dark:bg-emerald-500/[0.06]';
    case 'second':
      return 'border-l-4 border-l-orange-500 bg-orange-50/50 dark:bg-orange-500/[0.06]';
    case 'none':
      return 'border-l-4 border-l-red-500 bg-red-50/50 dark:bg-red-500/[0.06]';
    default:
      return 'border-l-4 border-l-transparent';
  }
}

function DrawList({
  title,
  subtitle,
  tone,
  info,
  rows,
  perfs,
  filter,
  onMove,
  moving,
  disabled,
  orderable,
  performanceId,
  onReorder,
  reordering,
  runOrder,
}: {
  title: string;
  subtitle?: string;
  tone?: 'red' | 'amber';
  // Optional explanatory tooltip rendered as an info icon next to the
  // section title. Used to clarify non-obvious buckets (e.g. the
  // "Not drawn / removed" section, which is distinct from "Draw outs").
  info?: React.ReactNode;
  rows: DraftEntryRow[];
  perfs: { performance_id: string; performance_name: string; is_slack: boolean }[];
  filter: FilterKey;
  onMove: (entry: DraftEntryRow, target: string) => void;
  moving: boolean;
  disabled: boolean;
  orderable?: boolean;
  performanceId?: string;
  onReorder?: (performanceId: string, entryIds: string[]) => void;
  reordering?: boolean;
  // Finals only: the "Run order" picker in the header plus, once a winnings
  // mode has been applied, the dollars each row was ordered by (keyed by
  // registration_id) and which figure they are ("YTD" / "current").
  runOrder?: {
    value: DrawRunOrderMode;
    onChange: (mode: DrawRunOrderMode) => void;
    pending: boolean;
    amounts: Record<string, number> | null;
    amountKind: 'YTD' | 'current' | null;
  };
}) {
  const visible = rows.filter((e) => matchesFilter(e, filter));
  // Drag-reorder needs the full, unfiltered list to be meaningful.
  const dndEnabled =
    !!orderable &&
    !!performanceId &&
    !!onReorder &&
    !disabled &&
    !reordering &&
    filter === 'all';
  // The list stays STATIC during a drag (never reorder mid-drag — that
  // moves the row under the cursor and causes the dragover/jitter loop).
  // Instead a single insertion line shows where the row will drop; the
  // actual reorder happens once, on drop.
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropIdx, setDropIdx] = useState<number | null>(null);

  const endDrag = () => {
    setDragId(null);
    setDropIdx(null);
  };
  const commitDrag = () => {
    if (dndEnabled && performanceId && onReorder && dragId && dropIdx != null) {
      const ids = visible.map((r) => r.id);
      const from = ids.indexOf(dragId);
      if (from !== -1) {
        const next = ids.filter((id) => id !== dragId);
        const insertAt = dropIdx > from ? dropIdx - 1 : dropIdx;
        next.splice(insertAt, 0, dragId);
        const changed = next.some((id, i) => id !== ids[i]);
        if (changed) onReorder(performanceId, next);
      }
    }
    endDrag();
  };

  // Header control: one-click sort the whole list by ferris weight.
  // asc = highest priority (lowest number) first; non-members always last.
  const sortByWeight = (dir: 'asc' | 'desc') => {
    if (!performanceId || !onReorder) return;
    const ids = [...visible]
      .sort((a, b) => {
        const an = a.priority_position == null;
        const bn = b.priority_position == null;
        if (an !== bn) return an ? 1 : -1; // non-members last
        if (an && bn) return 0;
        return dir === 'asc'
          ? (a.priority_position as number) - (b.priority_position as number)
          : (b.priority_position as number) - (a.priority_position as number);
      })
      .map((r) => r.id);
    onReorder(performanceId, ids);
  };

  const headTone =
    tone === 'red'
      ? 'bg-red-50 dark:bg-red-500/10 border-red-300 dark:border-red-900/60'
      : tone === 'amber'
        ? 'bg-amber-50 dark:bg-amber-500/5 border-amber-300 dark:border-amber-900/60'
        : 'bg-muted/40';
  return (
    <div className="rounded-lg border overflow-hidden">
      <div
        className={`flex items-center justify-between px-4 py-2 border-b ${headTone}`}
      >
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          <span>{title}</span>
          {info && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
                    aria-label={`About: ${title}`}
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  className="max-w-sm text-[11px] leading-snug whitespace-normal"
                >
                  {info}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {tone === 'red' && (
            <span className="ml-1 text-xs font-normal text-red-700 dark:text-red-400">
              needs placement
            </span>
          )}
        </h3>
        <div className="flex items-center gap-3">
          {runOrder && !disabled && (
            <Select
              value={runOrder.value}
              onValueChange={(v) => runOrder.onChange(v as DrawRunOrderMode)}
              disabled={runOrder.pending || !!reordering || rows.length < 2}
            >
              <SelectTrigger
                className="h-7 w-[170px] text-xs"
                aria-label={`Run order for ${title}`}
                title="Reorder this round by winnings. One-shot — rewrites the draw positions; you can still drag afterwards."
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RUN_ORDER_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {dndEnabled && visible.length > 1 && (
            <div
              className="flex items-center gap-0.5"
              title="Sort this list by ferris weight (priority)"
            >
              <button
                type="button"
                onClick={() => sortByWeight('asc')}
                className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                title="Highest priority first"
                aria-label="Sort by weight ascending"
              >
                <ArrowUpNarrowWide className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => sortByWeight('desc')}
                className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                title="Lowest priority first"
                aria-label="Sort by weight descending"
              >
                <ArrowDownNarrowWide className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          {subtitle && (
            <span className="text-xs text-muted-foreground">
              {subtitle}
              {filter !== 'all' && ` · ${visible.length} shown`}
            </span>
          )}
        </div>
      </div>
      {visible.length === 0 ? (
        <div className="px-4 py-3 text-xs text-muted-foreground italic">
          {rows.length === 0 ? 'empty' : 'none match the active filter'}
        </div>
      ) : (
        <div
          className="divide-y"
          onDragEnd={endDrag}
          onDragOver={
            dndEnabled
              ? (ev) => {
                  if (dragId) ev.preventDefault();
                }
              : undefined
          }
          onDrop={
            dndEnabled
              ? (ev) => {
                  ev.preventDefault();
                  commitDrag();
                }
              : undefined
          }
        >
          {visible.map((e, idx) => {
            const isNonMember = e.non_member;
            const isDragging = dragId === e.id;
            return (
              <Fragment key={e.id}>
                {dndEnabled && dropIdx === idx && (
                  <div className="h-[3px] bg-primary rounded mx-3 my-0.5" />
                )}
                <div
                  onDragOver={
                    dndEnabled
                      ? (ev) => {
                          if (!dragId) return;
                          ev.preventDefault();
                          ev.dataTransfer.dropEffect = 'move';
                          const r =
                            ev.currentTarget.getBoundingClientRect();
                          const after =
                            ev.clientY > r.top + r.height / 2;
                          const next = after ? idx + 1 : idx;
                          if (dropIdx !== next) setDropIdx(next);
                        }
                      : undefined
                  }
                  onDrop={
                    dndEnabled
                      ? (ev) => {
                          ev.preventDefault();
                          commitDrag();
                        }
                      : undefined
                  }
                  className={`px-4 py-2 flex items-center gap-3 text-sm transition-opacity duration-150 ${rowStyle(
                    e,
                  )} ${isDragging ? 'opacity-30' : ''}`}
                >
                {dndEnabled && (
                  <span
                    draggable
                    onDragStart={(ev) => {
                      setDragId(e.id);
                      ev.dataTransfer.effectAllowed = 'move';
                      ev.dataTransfer.setData('text/plain', e.id);
                    }}
                    className="shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground -ml-1"
                    title="Drag to reorder"
                    aria-label="Drag to reorder"
                  >
                    <GripVertical className="h-4 w-4" />
                  </span>
                )}
                <span className="w-8 text-xs text-muted-foreground tabular-nums">
                  #{dndEnabled ? idx + 1 : e.draw_position}
                </span>
                <div className="flex-1 min-w-0 leading-tight">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="truncate font-medium">
                      {e.is_team
                        ? (() => {
                            const primaryIsHeader =
                              e.team_position !== 'Heeler';
                            const headerName = primaryIsHeader
                              ? e.display_name
                              : e.team_partner_name;
                            const headerId = primaryIsHeader
                              ? e.user_profile_id
                              : e.team_partner_user_profile_id;
                            const heelerName = primaryIsHeader
                              ? e.team_partner_name
                              : e.display_name;
                            const heelerId = primaryIsHeader
                              ? e.team_partner_user_profile_id
                              : e.user_profile_id;
                            return (
                              <>
                                <ContestantNameLink
                                  name={headerName ?? '—'}
                                  userId={headerId}
                                />
                                {' & '}
                                <ContestantNameLink
                                  name={heelerName ?? '—'}
                                  userId={heelerId}
                                />
                              </>
                            );
                          })()
                        : (
                            <ContestantNameLink
                              name={e.display_name}
                              userId={e.user_profile_id}
                            />
                          )}
                    </span>
                    {e.buddy_group_number != null && (
                      <span
                        className={`shrink-0 text-[10px] font-semibold text-white px-1.5 py-0.5 rounded ${buddyColor(
                          e.buddy_group_number,
                        )}`}
                        title="Buddy group — shares the primary card's weight; must run the same performance"
                      >
                        BG {e.buddy_group_number}
                      </span>
                    )}
                    {e.is_team && (
                      <span
                        className="shrink-0 text-[10px] font-semibold text-white px-1.5 py-0.5 rounded bg-indigo-600"
                        title="Team roping — one draw slot per team; ordered by the primary partner's ferris weight"
                      >
                        Team
                      </span>
                    )}
                    {e.preference_outcome === 'draw_out' && (
                      <Badge
                        variant="destructive"
                        className="shrink-0 text-[10px] uppercase tracking-wide"
                      >
                        Drawn out
                      </Badge>
                    )}
                    {e.past_due_fees && e.past_due_fees.total_owed > 0 && (
                      <Badge
                        variant="destructive"
                        className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide bg-red-600 hover:bg-red-600 border border-red-800"
                        title={pastDueTooltip(e.past_due_fees)}
                        aria-label={`Past-due fees: owes ${formatMoney(
                          e.past_due_fees.total_owed,
                        )} from finished rodeos. Decide whether to keep them in the draw or move them out.`}
                      >
                        <AlertTriangle
                          className="h-3 w-3"
                          aria-hidden="true"
                        />
                        Fees due {formatMoney(e.past_due_fees.total_owed)}
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    1st: {formatPreferenceLabel(e.first_performance_preference) || '—'}
                    {'  ·  '}2nd: {formatPreferenceLabel(e.second_performance_preference) || '—'}
                    {e.is_team && (
                      <span className="text-indigo-600 dark:text-indigo-400">
                        {'  ·  '}Header:{' '}
                        {(e.team_position !== 'Heeler'
                          ? e.display_name
                          : e.team_partner_name) ?? '—'}
                        {e.team_position !== 'Heeler' && ' (primary)'}
                        {' · '}Heeler:{' '}
                        {(e.team_position !== 'Heeler'
                          ? e.team_partner_name
                          : e.display_name) ?? '—'}
                        {e.team_position === 'Heeler' && ' (primary)'}
                      </span>
                    )}
                  </div>
                  {/*
                    Why this row is unplaced. The backend records this on
                    every draft_draw_entry that fails placement so the
                    admin can tell at a glance whether the entry was
                    manually removed, was registered after the draw was
                    generated, couldn't fit anywhere, etc. We hide it for
                    drawn-out rows because the DRAWN OUT badge above
                    already explains them, and skip it for placed rows
                    where it would always be null.
                  */}
                  {e.preference_outcome !== 'draw_out' &&
                    !e.is_placed &&
                    e.unplaced_reason && (
                      <div className="text-[11px] italic text-red-700 dark:text-red-400 truncate">
                        Reason: {e.unplaced_reason}
                      </div>
                    )}
                </div>
                {e.preference_outcome &&
                  e.preference_outcome !== 'draw_out' &&
                  e.preference_outcome !== 'finals' && (
                  <span
                    className={`text-[10px] uppercase tracking-wide ${
                      e.preference_outcome === 'first'
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : e.preference_outcome === 'second'
                          ? 'text-orange-600 dark:text-orange-400'
                          : e.preference_outcome === 'none'
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-muted-foreground'
                    }`}
                  >
                    {e.preference_outcome === 'first'
                      ? '1st'
                      : e.preference_outcome === 'second'
                        ? '2nd'
                        : e.preference_outcome === 'none'
                          ? 'neither'
                          : ''}
                  </span>
                )}
                {isNonMember ? (
                  <Badge variant="outline" className="text-[10px]">
                    non-member
                  </Badge>
                ) : e.priority_position == null ? (
                  // Member-by-registration with no ferris weight. This is
                  // almost always a contestant whose self-claimed member
                  // number is still in `organization_memberships.pending`
                  // (the trust path sets `non_member=false` at signup but
                  // doesn't allocate an `association_priority` row until
                  // an admin approves). It can also surface a rare data
                  // gap — an approved member whose priority row never got
                  // created. Either way, rendering "—" was indistinguishable
                  // from "we have no info" and hid both cases; the explicit
                  // pill makes the unresolved state actionable for the
                  // admin without misrepresenting them as a non-member.
                  <Badge
                    variant="outline"
                    className="text-[10px] border-amber-500/60 text-amber-700 dark:text-amber-400 dark:border-amber-500/50"
                    title="Membership claim is pending admin approval — contestant has no ferris-wheel weight yet. Approve their membership to assign a priority position."
                  >
                    pending member
                  </Badge>
                ) : (
                  <span
                    className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400"
                    title={
                      e.buddy_group_number != null
                        ? 'Buddy group weight (shared from the primary card) — internal, never shown to contestants'
                        : 'Internal ferris-wheel weight — never shown to contestants'
                    }
                  >
                    <Trophy className="h-3 w-3" />
                    {e.priority_position}
                    {e.buddy_group_number != null && (
                      <span className="text-[9px] text-muted-foreground">grp</span>
                    )}
                  </span>
                )}
                {runOrder?.amounts && runOrder.amountKind && (
                  <span
                    className="shrink-0 text-xs text-muted-foreground tabular-nums"
                    title={
                      runOrder.amountKind === 'YTD'
                        ? 'Season winnings this row was ordered by'
                        : 'Winnings at this rodeo (earlier rounds) this row was ordered by'
                    }
                  >
                    {formatWholeDollars(runOrder.amounts[e.registration_id])}{' '}
                    {runOrder.amountKind}
                  </span>
                )}
                {e.manual_override && (
                  <Badge variant="secondary" className="text-[10px]">
                    manual
                  </Badge>
                )}
                <Select
                  value={e.performance_id ?? UNDRAWN}
                  onValueChange={(v) => onMove(e, v)}
                  disabled={moving || disabled}
                >
                  <SelectTrigger
                    className={`h-7 w-[150px] text-xs ${
                      e.performance_id
                        ? ''
                        : 'bg-red-600 border-red-700 text-white hover:bg-red-600 focus:ring-red-500 [&_svg]:text-white'
                    }`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNDRAWN}>— Not drawn —</SelectItem>
                    {perfs.map((p) => (
                      <SelectItem
                        key={p.performance_id}
                        value={p.performance_id}
                      >
                        {p.performance_name}
                        {p.is_slack ? ' (slack)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                </div>
              </Fragment>
            );
          })}
          {dndEnabled && dropIdx === visible.length && (
            <div className="h-[3px] bg-primary rounded mx-3 my-0.5" />
          )}
        </div>
      )}
    </div>
  );
}
